import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ReactGoogleAutocomplete from "react-google-autocomplete";

import { uploadFileToS3 } from "../../utils/s3Upload";
import AuthGate from "../../components/AuthGate/AuthGate";
import useUsedItemsQuery from "../../store/UsedItems/useUsedItemsQuery";
import "./UsedItems.css";
import "./UsedItemsMarqueeWidth.css";

const GOOGLE_MAPS_API_KEY = "AIzaSyDMC5uUfcSScgVY9AGqsGQwYm8P6_LmyYg";

const emptyPage = { results: [], count: 0 };

/* ── CUSTOM DROPDOWN — replaces native <select> for styled option panels ── */
function CustomDropdown({ value, onChange, options = [], placeholder = "Select", required = false, className = "" }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("touchstart", handler); };
  }, [open]);

  // Lift the parent usf-field-row above its siblings while panel is open
  React.useEffect(() => {
    const row = ref.current?.closest(".usf-field-row");
    if (!row) return;
    if (open) row.classList.add("usd-row-open");
    else row.classList.remove("usd-row-open");
    return () => row.classList.remove("usd-row-open");
  }, [open]);

  const selectedLabel = options.find((o) => String(o) === String(value)) || null;

  return (
    <div ref={ref} className={`usd-wrap ${className}`} style={{ position: "relative" }}>
      {/* Trigger button — visually identical to the existing input fields */}
      <button
        type="button"
        className={`usd-trigger ${open ? "open" : ""} ${!selectedLabel ? "placeholder" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="usd-trigger-text">{selectedLabel || placeholder}</span>
        <svg className={`usd-chevron ${open ? "flipped" : ""}`} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Hidden native input for form required-validation */}
      {required && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: "absolute", opacity: 0, height: 0, width: 0 }}
          required={required}
          value={value || ""}
          onChange={() => {}}
        />
      )}

      {/* Options panel */}
      {open && (
        <ul className="usd-panel" role="listbox">
          {/* Placeholder / empty option */}
          <li
            role="option"
            aria-selected={!value}
            className={`usd-option usd-option--placeholder ${!value ? "selected" : ""}`}
            onClick={() => { onChange(""); setOpen(false); }}
          >
            {placeholder}
          </li>
          {options.map((opt, idx) => (
            <li
              key={String(opt)}
              role="option"
              aria-selected={String(opt) === String(value)}
              className={`usd-option ${String(opt) === String(value) ? "selected" : ""} ${idx % 2 === 0 ? "even" : "odd"}`}
              onClick={() => { onChange(String(opt)); setOpen(false); }}
            >
              {String(opt) === String(value) && (
                <svg className="usd-check" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="4 12 9 17 20 7" />
                </svg>
              )}
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const extractList = (queryResult) => {
  const raw = queryResult?.data?.data || queryResult?.data || queryResult;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.results)) return raw.results;
  return [];
};

const extractData = (queryResult) => {
  const raw = queryResult?.data?.data || queryResult?.data || queryResult;
  if (Array.isArray(raw)) return { results: raw, count: raw.length };
  if (raw && typeof raw === "object") {
    return {
      results: Array.isArray(raw.results) ? raw.results : [],
      count: raw.count ?? (Array.isArray(raw.results) ? raw.results.length : 0),
    };
  }
  return emptyPage;
};

const apiError = (error) =>
  error?.response?.data?.message ||
  error?.data?.message ||
  error?.message ||
  "Something went wrong. Please try again.";

const loggedIn = () =>
  !!localStorage.getItem("accessToken") &&
  !!JSON.parse(localStorage.getItem("user") || "null")?.id;

const labelFor = (field) => field.name || field.key;

/* ── HAVERSINE DISTANCE CALCULATION (returns distance in KM) ── */
function calculateDistance(lat1, lon1, lat2, lon2) {
  if ([lat1, lon1, lat2, lon2].some((value) => value === null || value === undefined || value === "")) return null;
  
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/* ── GET USER LOCATION (from sessionStorage or browser geolocation) ── */
function getUserLocation() {
  try {
    const locationData = JSON.parse(sessionStorage.getItem('locationData') || '{}');
    if (locationData.latitude !== null && locationData.latitude !== undefined &&
        locationData.longitude !== null && locationData.longitude !== undefined) {
      return {
        latitude: Number(locationData.latitude),
        longitude: Number(locationData.longitude)
      };
    }
  } catch (e) {}
  
  // Fallback: try to get from browser geolocation
  return null;
}

/* ── DISTANCE SLIDER FOR USED ITEMS (0-600 KM range) ── */
function UsedItemsDistanceSlider({ value, onChange, onChangeCommitted }) {
  // Calculate the percentage for the active track (green portion)
  const percentage = (value / 600) * 100;

  return (
    <div className="used-items-distance-control">
      <div className="used-items-distance-slider-wrap">
        <div className="used-items-slider-container">
          <input
            type="range"
            min="0"
            max="600"
            step="10"
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value))}
            onMouseUp={(e) => onChangeCommitted(parseInt(e.target.value))}
            onTouchEnd={(e) => onChangeCommitted(parseInt(e.target.value))}
            className="used-items-distance-slider"
            style={{
              background: `linear-gradient(to right, #0f766e 0%, #0f766e ${percentage}%, #c8d8e8 ${percentage}%, #c8d8e8 100%)`
            }}
            aria-label="Distance range"
          />
        </div>
        <div className="used-items-distance-value">{value} KM</div>
      </div>
    </div>
  );
}

/* ── HEADER COMPONENT (no nav tabs — navigation moved to bottom bar) ── */
function Header({ title = "BUY & SELL", distance, onDistanceChange, onDistanceCommitted }) {
  const history = useHistory();
  const location = useLocation();
  const path = location.pathname;

  return (
    <header className="used-market-header-container">
      <div className="used-market-header-top">
        <button
          type="button"
          className="used-header-back-btn"
          onClick={() => {
            if (path === "/used-items") {
              history.push("/");
            } else {
              history.push("/used-items");
            }
          }}
          aria-label="Go back"
        >
          <ArrowBackIcon style={{ fontSize: 20 }} />
        </button>
        <h1 className="used-header-title">{title}</h1>
        <button
          className="used-header-profile-btn"
          aria-label="Share"
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: "Used Items Marketplace — MasterClick",
                text: "Buy & sell used items directly via WhatsApp on MasterClick Marketplace",
                url: window.location.href,
              }).catch(() => {});
            } else {
              // Fallback: copy URL to clipboard
              navigator.clipboard?.writeText(window.location.href).then(() => {
                alert("Page link copied to clipboard!");
              }).catch(() => {
                alert("Share URL: " + window.location.href);
              });
            }
          }}
        >
          {/* Share icon — white SVG */}
          <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>
      </div>
      
      {/* Distance Control Row — placed between title and main content */}
      {path === "/used-items" || path === "/used-items/" ? (
        <div className="used-market-header-distance-row">
          <UsedItemsDistanceSlider 
            value={distance} 
            onChange={onDistanceChange}
            onChangeCommitted={onDistanceCommitted}
          />
        </div>
      ) : null}
    </header>
  );
}

/* ── ITEM IMAGE CAROUSEL COMPONENT — compact horizontal marquee strip ── */
function ItemImageCarousel({ items }) {
  const history = useHistory();

  const carouselItems = React.useMemo(() => {
    if (!items || items.length === 0) return [];
    return items.filter(item => item.images && item.images.length > 0 && item.images[0]?.image);
  }, [items]);

  if (carouselItems.length === 0) return null;

  // Duplicate the list to create a seamless infinite loop
  const doubled = [...carouselItems, ...carouselItems];

  return (
    <div className="item-marquee-strip">
      <div className="item-marquee-track">
        {doubled.map((item, idx) => (
          <div
            key={`${item.id}-${idx}`}
            className="item-marquee-thumb"
            onClick={() => history.push(`/used-items/items/${item.id}`)}
            role="button"
            tabIndex={-1}
            aria-label={item.title}
          >
            <img
              className="item-marquee-img"
              src={item.images[0].image}
              alt={item.title || "Used item"}
              draggable={false}
              onError={(e) => { e.target.closest('.item-marquee-thumb').style.display = 'none'; }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── BOTTOM NAVIGATION BAR ── */
const VISITED_KEY = "used_items_visited";
const FAVORITES_KEY_PREFIX = "used_items_favorites_";

/* ── useFavorites — per-user localStorage favorites hook ── */
function getFavoritesKey() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.id ? `${FAVORITES_KEY_PREFIX}${user.id}` : `${FAVORITES_KEY_PREFIX}guest`;
  } catch (_) { return `${FAVORITES_KEY_PREFIX}guest`; }
}

function readFavorites() {
  try { return JSON.parse(localStorage.getItem(getFavoritesKey()) || "[]"); }
  catch (_) { return []; }
}

function writeFavorites(list) {
  try { localStorage.setItem(getFavoritesKey(), JSON.stringify(list)); } catch (_) {}
}

function useFavorites() {
  const [favorites, setFavorites] = React.useState(() => readFavorites());

  const isFav = React.useCallback((id) => favorites.some((f) => String(f.id) === String(id)), [favorites]);

  const toggle = React.useCallback((item) => {
    setFavorites((prev) => {
      const exists = prev.some((f) => String(f.id) === String(item.id));
      const next = exists
        ? prev.filter((f) => String(f.id) !== String(item.id))
        : [{ id: item.id, title: item.title, price: item.price, location: item.location || "", image: item.images?.[0]?.image || null, category: item.category?.name || "", path: `/used-items/items/${item.id}`, savedAt: Date.now() }, ...prev];
      writeFavorites(next);
      return next;
    });
  }, []);

  return { favorites, isFav, toggle };
}

function recordVisit(item) {
  try {
    const raw = localStorage.getItem(VISITED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    const entry = {
      id: item.id,
      title: item.title,
      price: item.price,
      image: item.images?.[0]?.image || null,
      category: item.category?.name || "",
      path: `/used-items/items/${item.id}`,
      visitedAt: Date.now(),
    };
    const filtered = list.filter((v) => v.id !== item.id);
    filtered.unshift(entry);
    localStorage.setItem(VISITED_KEY, JSON.stringify(filtered.slice(0, 30)));
  } catch (_) {}
}

function BottomNav() {
  const location = useLocation();
  const history = useHistory();
  const path = location.pathname;

  // Each item has its own independent active condition — never share between items
  const isHome     = path === "/used-items" || path === "/used-items/";
  const isCats     = false; // Categories scrolls on the home page — no dedicated route, never independently active
  const isSell     = path.startsWith("/used-items/sell") || path.startsWith("/used-items/items/my");
  const isVisited  = path === "/used-items/visited";
  const isProfile  = path === "/used-items/my-profile";

  const handleCategories = () => {
    if (path !== "/used-items" && path !== "/used-items/") {
      history.push("/used-items#categories");
      return;
    }
    const el = document.getElementById("used-categories-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="used-bottom-nav" aria-label="Main navigation">
      {/* HOME */}
      <button
        className={`used-bnav-item ${isHome ? "active" : ""}`}
        onClick={() => history.push("/used-items")}
        aria-label="Home"
      >
        <svg className="used-bnav-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
        </svg>
        <span>Home</span>
      </button>

      {/* CATEGORIES */}
      <button
        className={`used-bnav-item ${isCats ? "active" : ""}`}
        onClick={handleCategories}
        aria-label="Categories"
      >
        <svg className="used-bnav-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7a1 1 0 100 2 1 1 0 000-2z" />
        </svg>
        <span>Categories</span>
      </button>

      {/* CENTER + SELL */}
      <button
        className={`used-bnav-item used-bnav-center ${isSell ? "active" : ""}`}
        onClick={() => history.push(loggedIn() ? "/used-items/sell" : "/auth")}
        aria-label="Sell Item"
      >
        <div className="used-bnav-plus">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" width="33" height="33">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </div>
      </button>

      {/* VISITED / HISTORY */}
      <button
        className={`used-bnav-item ${isVisited ? "active" : ""}`}
        onClick={() => history.push("/used-items/visited")}
        aria-label="Visited"
      >
        <svg className="used-bnav-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 6h-2.18c.07-.44.18-.86.18-1.3C18 2.55 15.45 1 13 1c-1.31 0-2.55.52-3.46 1.43L8 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V8a2 2 0 00-2-2zm-8 13l-4-4h2.5v-4h3v4H16l-4 4z" />
        </svg>
        <span>Visited</span>
      </button>

      {/* PROFILE */}
      <button
        className={`used-bnav-item ${isProfile ? "active" : ""}`}
        onClick={() => history.push(loggedIn() ? "/used-items/my-profile" : "/auth")}
        aria-label="Profile"
      >
        <svg className="used-bnav-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
        </svg>
        <span>Profile</span>
      </button>
    </nav>
  );
}

function Message({ children, error = false }) {
  return (
    <div className={`used-message-box ${error ? "error" : ""}`}>
      {error ? (
        <ErrorOutlineIcon style={{ fontSize: 28, color: "#d32f2f", marginBottom: 6 }} />
      ) : null}
      <div>{children}</div>
    </div>
  );
}

/* ── DYNAMIC CATEGORY FIELD RENDERER ── */
function DynamicFields({ fields = [], values = {}, onChange, readOnly = false }) {
  if (!fields || fields.length === 0) return null;

  return (
    <div className="used-dynamic-fields-container">
      {readOnly ? (
        <div className="used-specs-grid">
          {fields.map((field) => {
            const rawVal = values[field.key];
            const displayVal =
              rawVal === undefined || rawVal === "" || rawVal === null
                ? "-"
                : typeof rawVal === "boolean"
                ? rawVal
                  ? "Yes"
                  : "No"
                : String(rawVal);
            return (
              <div key={field.id || field.key} className="used-spec-item">
                <span className="used-spec-label">{labelFor(field)}</span>
                <span className="used-spec-value">{displayVal}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="used-form-fields-group">
          {fields.map((field) => {
            const isReq = field.is_required;
            const currentVal = values[field.key] ?? "";

            return (
              <div key={field.id || field.key} className="used-input-group">
                <label className="used-input-label">
                  {labelFor(field)}
                  {isReq && <span className="used-required"> *</span>}
                </label>

                {field.field_type === "dropdown" ? (
                  <CustomDropdown
                    required={isReq}
                    className="usd-form-field"
                    value={currentVal}
                    onChange={(val) => onChange(field.key, val)}
                    options={field.options || []}
                    placeholder={`Select ${labelFor(field)}`}
                  />
                ) : field.field_type === "boolean" ? (
                  <div className="used-checkbox-row">
                    <input
                      type="checkbox"
                      id={`chk_${field.key}`}
                      checked={!!values[field.key]}
                      onChange={(e) => onChange(field.key, e.target.checked)}
                    />
                    <label htmlFor={`chk_${field.key}`}>{labelFor(field)}</label>
                  </div>
                ) : (
                  <input
                    required={isReq}
                    className="used-text-input"
                    type={
                      field.field_type === "number" || field.field_type === "decimal"
                        ? "number"
                        : field.field_type === "date"
                        ? "date"
                        : "text"
                    }
                    placeholder={`Enter ${labelFor(field)}`}
                    value={currentVal}
                    onChange={(e) => onChange(field.key, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── ITEM CARD COMPONENT ── */
function ItemCard({ item }) {
  const history = useHistory();
  const { isFav, toggle } = useFavorites();
  const faved = isFav(item.id);
  const mainImg = item.images?.[0]?.image || "/thumbnail.png";

  return (
    <article
      className="used-item-card"
      onClick={() => history.push(`/used-items/items/${item.id}`)}
    >
      <div className="used-item-card-image-wrap">
        <img
          className="used-item-card-img"
          src={mainImg}
          alt={item.title}
          onError={(e) => {
            e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
          }}
        />
        {item.category?.name && (
          <span className="used-item-category-tag">{item.category.name}</span>
        )}
        {/* Heart / Favorite button */}
        <button
          className={`used-fav-btn ${faved ? "faved" : ""}`}
          aria-label={faved ? "Remove from favorites" : "Add to favorites"}
          onClick={(e) => { e.stopPropagation(); toggle(item); }}
        >
          {faved ? "♥" : "♡"}
        </button>
      </div>

      <div className="used-item-card-content">
        <h3 className="used-item-card-title">{item.title}</h3>
        <div className="used-item-card-price">₹{Number(item.price).toLocaleString("en-IN")}</div>
        {item.location && (
          <div className="used-item-card-location">
            <LocationOnIcon style={{ fontSize: 13, marginRight: 2 }} />
            <span>{item.location}</span>
          </div>
        )}
        <p className="used-item-card-desc">
          {item.description ? item.description.slice(0, 75) + "..." : "No description provided."}
        </p>
      </div>
    </article>
  );
}

/* ── BROWSE MARKETPLACE SCREEN ── */
/* ── ACTIVE SUBSCRIPTION BANNER (shown on /used-items landing page) ── */
function ActiveSubBanner() {
  const subscriptionsQuery = useUsedItemsQuery.subscriptions();

  if (!loggedIn()) return (
    <div className="used-active-sub-banner used-active-sub-banner--compact" style={{ background: "#fff8e1", border: "1px solid #ffe082" }}>
      <div className="used-active-sub-banner-left">
        <ErrorOutlineIcon style={{ fontSize: 18, color: "#f59e0b", marginRight: 6, flexShrink: 0 }} />
        <div>
          <span className="used-active-sub-banner-title" style={{ color: "#92400e" }}>Not logged in</span>
          <span className="used-active-sub-banner-sub" style={{ color: "#b45309" }}>Login to sell your used items</span>
        </div>
      </div>
    </div>
  );
  if (subscriptionsQuery.isLoading || subscriptionsQuery.isError) return null;

  const activeSub = extractList(subscriptionsQuery).find((s) => {
    if (String(s.status || "").toLowerCase() !== "active") return false;
    if (s.plan?.is_active === false) return false;
    if (s.end_date && new Date(s.end_date) <= new Date()) return false;
    const ptype = String(s.plan_type_snapshot || s.plan?.plan_type || "").toLowerCase();
    return ptype === "item" || ptype === "time";
  });

  if (!activeSub) return null;

  const isItem = String(activeSub.plan_type_snapshot || "").toLowerCase() === "item";
  const itemLimit = activeSub.item_limit_snapshot || activeSub.plan?.item_limit || 0;
  const remaining = isItem
    ? Math.max(0, itemLimit - (activeSub.items_used || 0))
    : null;

  return (
    <div className="used-active-sub-banner used-active-sub-banner--compact">
      <div className="used-active-sub-banner-left">
        <CheckCircleIcon style={{ fontSize: 18, color: "#0f766e", marginRight: 6, flexShrink: 0 }} />
        <div>
          <span className="used-active-sub-banner-title">
            Active Plan: <strong>{activeSub.plan?.name || "Subscription"}</strong>
          </span>
          <span className="used-active-sub-banner-sub">
            {isItem
              ? `${activeSub.items_used || 0} used / ${itemLimit} allowed · ${remaining} remaining`
              : activeSub.end_date
              ? `Valid until ${new Date(activeSub.end_date).toLocaleDateString()}`
              : "Unlimited"}
          </span>
        </div>
      </div>
    </div>
  );
}

function Browse() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [specFilters, setSpecFilters] = useState({});
  const [distance, setDistance] = useState(600); // Default 600 KM (max) to show all items
  const [committedDistance, setCommittedDistance] = useState(600);

  // Load the complete matching source once, then reveal it incrementally while scrolling.
  // This is necessary because distance sorting must happen before any visible batch is sliced.
  const BATCH = 12;
  const [allItems, setAllItems] = useState([]);
  const [visibleCount, setVisibleCount] = useState(BATCH);
  const sentinelRef = React.useRef(null);
  const filterKey = `${search}|${category}|${minPrice}|${maxPrice}|${JSON.stringify(specFilters)}`;
  const lastFilterKey = React.useRef(filterKey);

  const categories = useUsedItemsQuery.categories({ limit: 100, offset: 0 });
  const itemQuery = {
    offset: 0,
    search,
    category,
    min_price: minPrice,
    max_price: maxPrice,
  };
  const initialItems = useUsedItemsQuery.items({ ...itemQuery, limit: 1 });
  const initialData = extractData(initialItems);
  const completeItems = useUsedItemsQuery.items({
    ...itemQuery,
    limit: Math.max(1, Number(initialData.count) || 1),
  });
  const items = Number(initialData.count) > 1 ? completeItems : initialItems;

  const categoriesList = extractList(categories);
  const selectedCategoryObj = categoriesList.find(
    (c) => String(c.id) === String(category)
  );

  // ── Reset the visible window whenever source filters change ─────────────────
  React.useEffect(() => {
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey;
      setAllItems([]);
      setVisibleCount(BATCH);
    }
  }, [filterKey]);

  // ── Replace the source collection and remove any duplicate API records ─────
  React.useEffect(() => {
    if (items.isLoading || items.isFetching) return;
    if (!items.data) return;
    const uniqueItems = new Map();
    (extractData(items).results || []).forEach((item) => uniqueItems.set(String(item.id), item));
    setAllItems(Array.from(uniqueItems.values()));
  }, [items.data, items.isLoading, items.isFetching]);

  const userLocation = getUserLocation();
  const handleSpecChange = (key, value) => {
    setSpecFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearSpecFilters = () => setSpecFilters({});

  const filteredResults = React.useMemo(() => {
    const matchingItems = allItems.filter((item) => {
      const numericPrice = Number(item.price);
      if (minPrice !== "" && numericPrice < Number(minPrice)) return false;
      if (maxPrice !== "" && numericPrice > Number(maxPrice)) return false;

      const itemDistance = userLocation && item.latitude !== null && item.latitude !== undefined &&
        item.longitude !== null && item.longitude !== undefined
        ? calculateDistance(userLocation.latitude, userLocation.longitude, item.latitude, item.longitude)
        : null;
      if (itemDistance !== null && itemDistance > committedDistance) return false;

      if (!selectedCategoryObj?.fields?.length) return true;
      return Object.entries(specFilters).every(([key, filterVal]) => {
        if (filterVal === "" || filterVal === undefined || filterVal === null) return true;
        const itemVal = item.field_values?.[key];
        if (itemVal === undefined || itemVal === null || itemVal === "") return false;
        const strItemVal = String(itemVal).trim().toLowerCase();
        const strFilterVal = String(filterVal).trim().toLowerCase();
        return strItemVal === strFilterVal || strItemVal.includes(strFilterVal);
      });
    });

    return matchingItems.sort((first, second) => {
      const firstDistance = userLocation && first.latitude !== null && first.latitude !== undefined &&
        first.longitude !== null && first.longitude !== undefined
        ? calculateDistance(userLocation.latitude, userLocation.longitude, first.latitude, first.longitude)
        : null;
      const secondDistance = userLocation && second.latitude !== null && second.latitude !== undefined &&
        second.longitude !== null && second.longitude !== undefined
        ? calculateDistance(userLocation.latitude, userLocation.longitude, second.latitude, second.longitude)
        : null;
      if (firstDistance === null && secondDistance !== null) return 1;
      if (firstDistance !== null && secondDistance === null) return -1;
      if (firstDistance !== null && secondDistance !== null && firstDistance !== secondDistance) {
        return firstDistance - secondDistance;
      }
      return String(first.id).localeCompare(String(second.id), undefined, { numeric: true });
    });
  }, [allItems, committedDistance, maxPrice, minPrice, selectedCategoryObj, specFilters, userLocation]);

  const visibleResults = filteredResults.slice(0, visibleCount);
  const hasMore = visibleCount < filteredResults.length;

  // ── IntersectionObserver — reveals the next local batch at the page end ────
  React.useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !items.isLoading && !items.isFetching) {
          setVisibleCount((prev) => Math.min(prev + BATCH, filteredResults.length));
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [filteredResults.length, hasMore, items.isLoading, items.isFetching]);

  return (
    <div className="used-market-page">
      <Header 
        title="BUY & SELL" 
        distance={distance}
        onDistanceChange={setDistance}
        onDistanceCommitted={setCommittedDistance}
      />

      <main className="used-market-main-content">
        {/* Banner */}
        <div className="used-market-banner">
          <div className="used-market-banner-text">
            <span className="used-market-subheading">SECOND-HAND MARKET</span>
            <h2>Buy & Sell Used Items</h2>
            <p className="used-safety-notice">
              <span className="used-safety-en">⚠️ All buying and selling transactions are at the users' own responsibility. Verify the item before making any advance or payment. MasterClick is not responsible for any transaction or loss.</span>
              <span className="used-safety-ml">⚠️ എല്ലാ ഇടപാടുകളും സ്വന്തം ഉത്തരവാദിത്തത്തിൽ; വസ്തു പരിശോധിച്ച് ഉറപ്പാക്കാതെ അഡ്വാൻസ്/പേയ്മെന്റ് നൽകാതിരിക്കുക.</span>
            </p>
          </div>
        </div>

        {/* Item Image Carousel */}
        <ItemImageCarousel items={allItems} />

        {/* Active Subscription Banner + Search Bar — Combined Row */}
        <div className="used-status-search-row">
          <ActiveSubBanner />
          
          {/* Search Bar */}
          <div className="used-search-box-wrap">
            <SearchIcon className="used-search-icon" />
            <input
              className="used-search-input"
              placeholder="Search bikes, cars, flats, mobiles..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
            {search && (
              <button className="used-search-clear" onClick={() => setSearch("")}>
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        </div>

        {/* ALL CATEGORIES GRID SECTION FROM BACKEND */}
        <section className="used-section-block" id="used-categories-section">
          <div className="used-section-header">
            <h3 style={{ fontSize: "8.32px" }}>
              <CategoryIcon style={{ fontSize: 26, verticalAlign: "middle", marginRight: 6, color: "#0f766e" }} />
              All Categories ({categoriesList.length})
            </h3>
            {category && (
              <button
                className="used-text-btn"
                onClick={() => {
                  setCategory("");
                  setSpecFilters({});
                }}
              >
                Clear Selected Category
              </button>
            )}
          </div>

          {categories.isLoading ? (
            <Message>Loading categories...</Message>
          ) : categories.isError ? (
            <Message error>{apiError(categories.error)}</Message>
          ) : categoriesList.length > 0 ? (
            <div className="used-categories-grid">
              <div
                className={`used-cat-grid-card ${!category ? "selected" : ""}`}
                onClick={() => {
                  setCategory("");
                  setSpecFilters({});
                }}
              >
                <div className="used-cat-card-icon">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" style={{display:"block"}}>
                    {/* ── Soft drop shadow ── */}
                    <ellipse cx="32" cy="58" rx="18" ry="4" fill="rgba(30,80,200,0.13)" />

                    {/* ── Back box (deepest layer) ── */}
                    <rect x="22" y="18" width="26" height="22" rx="4" fill="url(#allItemsGrad1)" opacity="0.72" />
                    <rect x="22" y="18" width="26" height="22" rx="4" stroke="url(#allItemsStroke1)" strokeWidth="1.1" fill="none" />
                    {/* back box highlight edge */}
                    <rect x="23" y="19" width="24" height="4" rx="2" fill="white" opacity="0.18" />

                    {/* ── Mid box ── */}
                    <rect x="16" y="22" width="26" height="22" rx="4" fill="url(#allItemsGrad2)" opacity="0.82" />
                    <rect x="16" y="22" width="26" height="22" rx="4" stroke="url(#allItemsStroke2)" strokeWidth="1.2" fill="none" />
                    {/* mid box top gloss */}
                    <rect x="17" y="23" width="24" height="4.5" rx="2" fill="white" opacity="0.22" />

                    {/* ── Front box (foremost layer) ── */}
                    <rect x="10" y="26" width="28" height="24" rx="5" fill="url(#allItemsGrad3)" />
                    <rect x="10" y="26" width="28" height="24" rx="5" stroke="url(#allItemsStroke3)" strokeWidth="1.3" fill="none" />

                    {/* front box crystal top-face gloss */}
                    <rect x="11.5" y="27.5" width="25" height="6" rx="2.5" fill="white" opacity="0.30" />

                    {/* subtle inner light refraction line */}
                    <line x1="14" y1="34" x2="35" y2="34" stroke="white" strokeWidth="0.8" opacity="0.18" strokeLinecap="round" />

                    {/* bottom-right inner shadow for depth */}
                    <rect x="10" y="38" width="28" height="12" rx="0 0 5 5" fill="url(#allItemsShadow)" opacity="0.30" />

                    {/* ── Star / sparkle on top-right corner ── */}
                    <g transform="translate(37,19)" opacity="0.90">
                      <line x1="0" y1="-5" x2="0" y2="5" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
                      <line x1="-5" y1="0" x2="5" y2="0" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
                      <line x1="-3.5" y1="-3.5" x2="3.5" y2="3.5" stroke="white" strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
                      <line x1="3.5" y1="-3.5" x2="-3.5" y2="3.5" stroke="white" strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
                    </g>

                    {/* ── Small crystal gem on front face ── */}
                    <polygon points="24,31 30,31 33,36 24,36 21,36" fill="white" opacity="0.13" />
                    <polygon points="24,31 30,31 27,34" fill="white" opacity="0.22" />

                    {/* ── Gradient & stroke defs ── */}
                    <defs>
                      {/* back box */}
                      <linearGradient id="allItemsGrad1" x1="22" y1="18" x2="48" y2="40" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#a8c8ff" />
                        <stop offset="100%" stopColor="#3b6fe0" />
                      </linearGradient>
                      <linearGradient id="allItemsStroke1" x1="22" y1="18" x2="48" y2="40" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#c8dcff" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#2a50c8" stopOpacity="0.4" />
                      </linearGradient>
                      {/* mid box */}
                      <linearGradient id="allItemsGrad2" x1="16" y1="22" x2="42" y2="44" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#bdd6ff" />
                        <stop offset="100%" stopColor="#2f5ed9" />
                      </linearGradient>
                      <linearGradient id="allItemsStroke2" x1="16" y1="22" x2="42" y2="44" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#d8eaff" stopOpacity="0.9" />
                        <stop offset="100%" stopColor="#2040b0" stopOpacity="0.4" />
                      </linearGradient>
                      {/* front box */}
                      <linearGradient id="allItemsGrad3" x1="10" y1="26" x2="38" y2="50" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#d6eaff" />
                        <stop offset="45%" stopColor="#5a9af8" />
                        <stop offset="100%" stopColor="#1a44c8" />
                      </linearGradient>
                      <linearGradient id="allItemsStroke3" x1="10" y1="26" x2="38" y2="50" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#e8f3ff" stopOpacity="1" />
                        <stop offset="100%" stopColor="#1830a0" stopOpacity="0.5" />
                      </linearGradient>
                      {/* bottom shadow on front face */}
                      <linearGradient id="allItemsShadow" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                        <stop offset="0%" stopColor="#0a2080" stopOpacity="0" />
                        <stop offset="100%" stopColor="#0a2080" stopOpacity="0.45" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <span className="used-cat-card-name">All Items</span>
              </div>
              {categoriesList.map((cat) => (
                <div
                  key={cat.id}
                  className={`used-cat-grid-card ${
                    String(cat.id) === String(category) ? "selected" : ""
                  }`}
                  onClick={() => {
                    setCategory(cat.id);
                    setSpecFilters({});
                  }}
                >
                  <div className="used-cat-card-icon">
                    {cat.image ? (
                      <img
                        src={cat.image}
                        alt={cat.name}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <span>📦</span>
                    )}
                  </div>
                  <span className="used-cat-card-name">{cat.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <Message>No categories found from backend.</Message>
          )}
        </section>

        {/* Filters Row */}
        <section className="used-section-block">
          <div className="used-filter-bar">
            <div className="used-filter-title">
              <FilterListIcon style={{ fontSize: 18 }} /> Price Filter
            </div>
            <div className="used-price-inputs">
              <input
                type="number"
                min="0"
                placeholder="Min ₹"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
              />
              <span>-</span>
              <input
                type="number"
                min="0"
                placeholder="Max ₹"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Dynamic Category Specs Filter */}
        {selectedCategoryObj?.fields?.length > 0 && (
          <section className="used-category-specs-filter">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h4 style={{ margin: 0 }}>Filter {selectedCategoryObj.name} Specs</h4>
              {Object.values(specFilters).some((v) => v !== "" && v !== undefined) && (
                <button
                  type="button"
                  className="used-text-btn"
                  onClick={clearSpecFilters}
                  style={{ fontSize: 12 }}
                >
                  Reset Specs
                </button>
              )}
            </div>
            <div className="used-specs-filter-grid">
              {selectedCategoryObj.fields.map((field) => (
                <div key={field.key} className="used-spec-filter-item">
                  <label>{labelFor(field)}</label>
                  {field.field_type === "dropdown" ? (
                    <select
                      value={specFilters[field.key] || ""}
                      onChange={(e) => handleSpecChange(field.key, e.target.value)}
                    >
                      <option value="">All</option>
                      {(field.options || []).map((opt) => (
                        <option key={String(opt)} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={
                        field.field_type === "number" || field.field_type === "decimal"
                          ? "number"
                          : "text"
                      }
                      placeholder={`Any ${labelFor(field)}`}
                      value={specFilters[field.key] || ""}
                      onChange={(e) => handleSpecChange(field.key, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Items Listing Grid */}
        <section className="used-section-block">
          <div className="used-section-header">
            <h3 style={{ fontSize: "9.6px" }}>
              {selectedCategoryObj ? `${selectedCategoryObj.name} Listings` : "Used Items Listing"}
            </h3>
            <span className="used-count-badge">
              {filteredResults.length} item{filteredResults.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* First-load state */}
          {items.isLoading && allItems.length === 0 ? (
            <Message>Loading items...</Message>
          ) : items.isError && allItems.length === 0 ? (
            <Message error>{apiError(items.error)}</Message>
          ) : filteredResults.length === 0 && !items.isLoading ? (
            <Message>No used items found matching your filter.</Message>
          ) : (
            <>
              <div className="used-items-grid-layout">
                {visibleResults.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
              </div>

              {/* Sentinel — IntersectionObserver watches this div */}
              <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />

              {/* Loading spinner while fetching next batch */}
              {items.isFetching && hasMore && (
                <div className="used-infinite-loader">
                  <span className="used-infinite-spinner" />
                  <span>Loading more items…</span>
                </div>
              )}

              {/* End of list message */}
              {!hasMore && allItems.length > 0 && (
                <p className="used-infinite-end">No more items available.</p>
              )}
            </>
          )}
        </section>
      </main>
      <BottomNav />
    </div>
  );
}

/* ── MORE FROM THIS CATEGORY — horizontal scroll row ── */
function MoreFromCategory({ categoryId, categoryName, currentItemId }) {
  const history = useHistory();
  const { isFav, toggle } = useFavorites();
  const query = useUsedItemsQuery.items({ category: categoryId, limit: 30 });
  const others = extractData(query).results.filter((item) => String(item.id) !== String(currentItemId));

  if (query.isLoading || !others.length) return null;

  return (
    <div className="used-detail-card-section">
      <h3 className="used-detail-section-title">
        More from {categoryName || "This Category"}
      </h3>
      <div className="used-mfc-scroll">
        {others.map((item) => {
          const img = item.images?.[0]?.image || null;
          const faved = isFav(item.id);
          return (
            <div
              key={item.id}
              className="used-mfc-card"
              onClick={() => history.push(`/used-items/items/${item.id}`)}
            >
              <div className="used-mfc-img-wrap">
                {img ? (
                  <img src={img} alt={item.title} onError={(e) => { e.target.src = "https://via.placeholder.com/120x90?text=No+Image"; }} />
                ) : (
                  <div className="used-mfc-img-placeholder">📷</div>
                )}
                <button
                  className={`used-fav-btn used-fav-btn--sm ${faved ? "faved" : ""}`}
                  aria-label={faved ? "Remove from favorites" : "Add to favorites"}
                  onClick={(e) => { e.stopPropagation(); toggle(item); }}
                >
                  {faved ? "♥" : "♡"}
                </button>
              </div>
              <div className="used-mfc-info">
                <div className="used-mfc-title">{item.title}</div>
                <div className="used-mfc-price">₹{Number(item.price).toLocaleString("en-IN")}</div>
                {item.location && (
                  <div className="used-mfc-location">
                    <LocationOnIcon style={{ fontSize: 10 }} />
                    {item.location}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── MORE ADS FROM THIS SELLER — matched by seller_mobile across all categories ── */
function MoreFromSeller({ sellerId, currentItemId, sellerName }) {
  /* Keep the old seller-ID based version as a fallback — replaced below */
  return null;
}

function MoreAdsFromSeller({ sellerMobile, currentItemId, sellerName }) {
  const history = useHistory();
  const { isFav, toggle } = useFavorites();

  // Normalise mobile: strip spaces/dashes but keep digits and leading +
  const normMobile = sellerMobile ? sellerMobile.replace(/[\s\-().]/g, "") : "";

  const query = useUsedItemsQuery.items({ seller_mobile: normMobile, limit: 50 });
  const allItems = extractData(query).results;
  const others = allItems.filter((item) => String(item.id) !== String(currentItemId));

  if (query.isLoading) return null;
  if (!normMobile || !others.length) return null;

  return (
    <div className="used-detail-card-section used-more-from-seller">
      <h3 className="used-detail-section-title">
        More Ads from {sellerName || "This Seller"}
      </h3>
      <div className="used-mfs-grid">
        {others.map((item) => {
          const img = item.images?.[0]?.image || null;
          const faved = isFav(item.id);
          return (
            <div
              key={item.id}
              className="used-mfs-card"
              onClick={() => history.push(`/used-items/items/${item.id}`)}
            >
              <div className="used-mfs-img-wrap" style={{ position: "relative" }}>
                {img ? (
                  <img src={img} alt={item.title} onError={(e) => { e.target.src = "https://via.placeholder.com/120x90?text=No+Image"; }} />
                ) : (
                  <div className="used-mfs-img-placeholder">📷</div>
                )}
                <button
                  className={`used-fav-btn used-fav-btn--sm ${faved ? "faved" : ""}`}
                  aria-label={faved ? "Remove from favorites" : "Add to favorites"}
                  onClick={(e) => { e.stopPropagation(); toggle(item); }}
                >
                  {faved ? "♥" : "♡"}
                </button>
              </div>
              <div className="used-mfs-info">
                {item.category?.name && (
                  <div style={{ fontSize: 9, fontWeight: 700, color: "var(--used-primary)", marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                    {item.category.name}
                  </div>
                )}
                <div className="used-mfs-title">{item.title}</div>
                <div className="used-mfs-price">₹{Number(item.price).toLocaleString("en-IN")}</div>
                {item.location && (
                  <div className="used-mfs-location">
                    <LocationOnIcon style={{ fontSize: 11 }} />
                    {item.location}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── ITEM DETAIL SCREEN ── */
function Detail() {
  const { id } = useParams();
  const itemQuery = useUsedItemsQuery.item(id);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [galleryTransitionEnabled, setGalleryTransitionEnabled] = useState(true);
  // ── Favorites hook must be called unconditionally before any early returns ──
  const { isFav: detailIsFav, toggle: detailToggle } = useFavorites();

  const data = itemQuery.data?.data || itemQuery.data;
  const images = data?.images || [];

  // Advance multi-photo listings every five seconds. The duplicate first slide lets
  // the last image continue left into the first without a visible reverse jump.
  useEffect(() => {
    setGalleryTransitionEnabled(true);
    setActiveImageIdx(0);

    if (images.length <= 1) return undefined;

    const slideshow = window.setInterval(() => {
      setGalleryTransitionEnabled(true);
      setActiveImageIdx((current) =>
        current >= images.length - 1 ? images.length : current + 1
      );
    }, 5000);

    return () => window.clearInterval(slideshow);
  }, [id, images.length]);

  // Record this item in visit history once data is loaded
  React.useEffect(() => {
    if (data?.id) recordVisit(data);
  }, [data?.id]);

  // Use seller's registered/login mobile (seller.mobile) as primary contact number
  const sellerPhone = (data?.seller?.mobile || data?.seller_mobile || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(
    `Hi ${data?.seller?.name || "Seller"}, I am interested in your item "${data?.title || "listing"}" listed for ₹${data?.price || 0} on Master-Mart Used Items Marketplace.`
  );

  if (itemQuery.isLoading)
    return (
      <div className="used-market-page">
        <Header title="Item Details" />
        <Message>Loading item details...</Message>
        <BottomNav />
      </div>
    );

  if (itemQuery.isError || !data)
    return (
      <div className="used-market-page">
        <Header title="Item Details" />
        <Message error>{apiError(itemQuery.error)}</Message>
        <BottomNav />
      </div>
    );

  const galleryImages = images.length > 1 ? [...images, images[0]] : images;
  const displayedImageIndex = activeImageIdx === images.length ? 0 : activeImageIdx;

  const resetGalleryAfterLoop = () => {
    if (activeImageIdx !== images.length) return;

    setGalleryTransitionEnabled(false);
    setActiveImageIdx(0);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setGalleryTransitionEnabled(true));
    });
  };

  const detailFaved = detailIsFav(data?.id);

  return (
    <div className="used-market-page">
      <Header title={data.title} />

      <main className="used-market-main-content used-detail-page">
        {/* Main Photo Display */}
        <div className="used-detail-gallery">
          {images.length > 1 ? (
            <div className="used-detail-gallery-viewport">
              <div
                className={`used-detail-gallery-track${galleryTransitionEnabled ? " is-animating" : ""}`}
                style={{ transform: `translateX(-${activeImageIdx * 100}%)` }}
                onTransitionEnd={resetGalleryAfterLoop}
              >
                {galleryImages.map((image, idx) => (
                  <img
                    key={`${image.id || image.image}-${idx}`}
                    className="used-detail-main-img"
                    src={image.image || "/thumbnail.png"}
                    alt={idx === displayedImageIndex ? data.title : ""}
                    aria-hidden={idx !== displayedImageIndex}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/600x400?text=No+Image";
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <img
              className="used-detail-main-img used-detail-main-img--single"
              src={images[0]?.image || "/thumbnail.png"}
              alt={data.title}
              onError={(e) => {
                e.target.src = "https://via.placeholder.com/600x400?text=No+Image";
              }}
            />
          )}

          {images.length > 1 && (
            <div className="used-detail-thumbnails-row">
              {images.map((img, idx) => (
                <img
                  key={img.id || idx}
                  src={img.image}
                  alt=""
                  className={`used-thumbnail-img ${idx === displayedImageIndex ? "active" : ""}`}
                  onClick={() => {
                    setGalleryTransitionEnabled(true);
                    setActiveImageIdx(idx);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Title & Price Header */}
        <div className="used-detail-card-section">
          <div className="used-detail-heading-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 className="used-detail-item-title">{data.title}</h2>
              {data.category?.name && (
                <span className="used-detail-cat-badge">{data.category.name}</span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
              <div className="used-detail-price-tag">
                ₹{Number(data.price).toLocaleString("en-IN")}
              </div>
              {/* ── Favorite heart button ── */}
              <button
                className={`used-fav-btn used-fav-btn--detail ${detailFaved ? "faved" : ""}`}
                aria-label={detailFaved ? "Remove from favorites" : "Add to favorites"}
                onClick={() => detailToggle(data)}
                style={{ position: "static" }}
              >
                {detailFaved ? "♥" : "♡"}
              </button>
            </div>
          </div>

          {data.location && (
            <div className="used-detail-location">
              <LocationOnIcon style={{ fontSize: 16, color: "#0f766e" }} />
              <span>{data.location}</span>
            </div>
          )}
        </div>

        {/* Item Description */}
        <div className="used-detail-card-section">
          <h3 className="used-detail-section-title">Description</h3>
          <p className="used-detail-description-text">
            {data.description || "No description available for this listing."}
          </p>
        </div>

        {/* Dynamic Specifications */}
        {data.category?.fields && data.category.fields.length > 0 && (
          <div className="used-detail-card-section">
            <h3 className="used-detail-section-title">Specifications</h3>
            <DynamicFields
              fields={data.category.fields}
              values={data.field_values || {}}
              readOnly
            />
          </div>
        )}

        {/* Seller Info & Contact */}
        <div className="used-detail-card-section">
          <h3 className="used-detail-section-title">Seller Information</h3>
          <div className="used-seller-info-box">
            <div className="used-seller-avatar">
              <PersonIcon style={{ fontSize: 24, color: "#0f766e" }} />
            </div>
            <div className="used-seller-details">
              <strong>{data.seller?.name || "Verified Seller"}</strong>
              <span>
                <PhoneIcon style={{ fontSize: 13, marginRight: 4 }} />
                {data.seller_mobile || "Mobile available on WhatsApp"}
              </span>
            </div>
          </div>
        </div>

        {/* ── More from This Category (same category, all sellers, horizontal scroll) ── */}
        {data.category?.id && (
          <MoreFromCategory
            categoryId={data.category.id}
            categoryName={data.category.name}
            currentItemId={data.id}
          />
        )}

        {/* ── More Ads from This Seller (matched by mobile across all categories) ── */}
        {(data.seller_mobile || data.seller?.mobile) && (
          <MoreAdsFromSeller
            sellerMobile={data.seller_mobile || data.seller?.mobile}
            currentItemId={data.id}
            sellerName={data.seller?.name}
          />
        )}

        {/* Chat → Call → WhatsApp Action Buttons */}
        <div className="used-detail-action-bar">
          <div className="used-detail-contact-row">
            {/* Chat — primary teal color; handler can be wired to backend later */}
            <button
              className="used-btn-chat"
              aria-label="Chat with seller"
              onClick={() => {
                // TODO: connect to chat backend when ready
                alert("Chat feature coming soon!");
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" style={{ marginRight: 6, flexShrink: 0 }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Chat
            </button>
            <a
              className={`used-btn-call ${!sellerPhone ? "disabled" : ""}`}
              href={sellerPhone ? `tel:${sellerPhone}` : "#"}
              onClick={(e) => { if (!sellerPhone) e.preventDefault(); }}
            >
              <PhoneIcon style={{ fontSize: 20, marginRight: 6 }} />
              Call
            </a>
            <a
              className={`used-btn-whatsapp ${!sellerPhone ? "disabled" : ""}`}
              href={sellerPhone ? `https://wa.me/${sellerPhone}?text=${waMsg}` : "#"}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => { if (!sellerPhone) e.preventDefault(); }}
            >
              <WhatsAppIcon style={{ fontSize: 20, marginRight: 6 }} />
              WhatsApp
            </a>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}

/* ── SELLER PLAN SELECTION & PAYMENT WIZARD ── */
function Sell() {
  const history = useHistory();
  const [categoryId, setCategoryId] = useState("");
  const [plan, setPlan] = useState(null);
  const [step, setStep] = useState("category"); // category -> plan -> payment -> pending
  const [subscription, setSubscription] = useState(null);
  const [payment, setPayment] = useState({ transaction_id: "", proof_url: "" });
  const [uploadingProof, setUploadingProof] = useState(false);
  const [notice, setNotice] = useState("");

  const categories = useUsedItemsQuery.categories({ limit: 100, offset: 0 });
  const plans = useUsedItemsQuery.plans();
  const createSubscription = useUsedItemsQuery.createSubscription();
  const createPayment = useUsedItemsQuery.createPayment();

  const categoriesList = extractList(categories);

  const plansList = (plans.data?.data?.results || plans.data?.results || []).filter(
    (p) => p.is_active !== false
  );

  const handlePlanSubmit = async () => {
    if (!plan) return;
    setNotice("");
    try {
      const res = await createSubscription.mutateAsync({ plan_id: plan.id });
      const subObj = res?.data || res;
      setSubscription(subObj);

      if (Number(plan.price) === 0) {
        // Free plan auto-activated
        history.replace(`/used-items/sell/item?category=${categoryId}`);
      } else {
        // Paid plan -> payment step
        setStep("payment");
      }
    } catch (err) {
      setNotice(apiError(err));
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProof(true);
    setNotice("");
    try {
      const url = await uploadFileToS3(file);
      setPayment((prev) => ({ ...prev, proof_url: url }));
    } catch (err) {
      setNotice("Failed to upload proof image: " + err.message);
    } finally {
      setUploadingProof(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!payment.transaction_id || !payment.proof_url) {
      setNotice("Please provide transaction ID and upload payment proof.");
      return;
    }

    setNotice("");
    try {
      const res = await createPayment.mutateAsync({
        subscription_id: subscription.id,
        transaction_id: payment.transaction_id,
        proof_url: payment.proof_url,
      });

      const resData = res?.data || res;
      setNotice("Payment Verification Submitted!");
      setPayment((prev) => ({ ...prev, whatsapp_url: resData.whatsapp_url }));
      setStep("pending");
    } catch (err) {
      setNotice(apiError(err));
    }
  };

  return (
    <AuthGate>
      <div className="used-market-page">
        <Header title="Sell Used Item" />

        <main className="used-market-main-content">
          {/* Stepper Bar */}
          <div className="used-wizard-stepper">
            <div className={`used-step-item ${step === "category" ? "active" : ""}`}>
              1. Category
            </div>
            <div className={`used-step-item ${step === "plan" ? "active" : ""}`}>
              2. Plan
            </div>
            <div
              className={`used-step-item ${
                step === "payment" || step === "pending" ? "active" : ""
              }`}
            >
              3. Payment
            </div>
          </div>

          {notice && <Message error={step !== "pending"}>{notice}</Message>}

          {/* STEP 1: CATEGORY SELECTION */}
          {step === "category" && (
            <section className="used-card-box">
              <h2 className="used-card-box-title">Select Item Category</h2>
              <p className="used-muted-text">
                Choose the category that best describes your used item.
              </p>

              <div className="used-category-grid-select">
                {categoriesList.map((cat) => (
                  <div
                    key={cat.id}
                    className={`used-cat-select-card ${
                      String(cat.id) === String(categoryId) ? "selected" : ""
                    }`}
                    onClick={() => setCategoryId(cat.id)}
                  >
                    {cat.image && (
                      <img
                        src={cat.image}
                        alt=""
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    )}
                    <span>{cat.name}</span>
                  </div>
                ))}
              </div>

              <div className="used-form-actions-bar">
                <button
                  className="used-primary-btn"
                  disabled={!categoryId}
                  onClick={() => setStep("plan")}
                >
                  Continue to Subscription Plans ›
                </button>
              </div>
            </section>
          )}

          {/* STEP 2: SUBSCRIPTION PLAN SELECTION */}
          {step === "plan" && (
            <section className="used-card-box">
              <h2 className="used-card-box-title">Choose Subscription Plan</h2>
              <p className="used-muted-text">
                Select a plan to list items on the marketplace. Free plans auto-activate immediately!
              </p>

              <div className="used-plans-cards-grid">
                {plansList.map((p) => {
                  const isFree = Number(p.price) === 0;
                  const isSelected = plan?.id === p.id;
                  return (
                    <div
                      key={p.id}
                      className={`used-plan-card ${isSelected ? "selected" : ""}`}
                      onClick={() => setPlan(p)}
                    >
                      <div className="used-plan-card-header">
                        <h4>{p.name}</h4>
                        <span className={`used-plan-price-badge ${isFree ? "free" : ""}`}>
                          {isFree ? "FREE" : `₹${Number(p.price).toLocaleString("en-IN")}`}
                        </span>
                      </div>

                      <div className="used-plan-card-details">
                        {p.plan_type === "item" ? (
                          <div className="used-plan-feature">
                            <CheckCircleIcon style={{ fontSize: 16, color: "#0f766e" }} />
                            <span>{p.item_limit} Items Listing Quota</span>
                          </div>
                        ) : (
                          <div className="used-plan-feature">
                            <CheckCircleIcon style={{ fontSize: 16, color: "#0f766e" }} />
                            <span>
                              Unlimited Items for {p.duration_days} Days
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="used-form-actions-bar">
                <button
                  className="used-secondary-btn"
                  onClick={() => setStep("category")}
                >
                  ‹ Back
                </button>
                <button
                  className="used-primary-btn"
                  disabled={!plan || createSubscription.isLoading}
                  onClick={handlePlanSubmit}
                >
                  {createSubscription.isLoading ? "Processing..." : "Select Plan ›"}
                </button>
              </div>
            </section>
          )}

          {/* STEP 3: MANUAL PAYMENT SUBMISSION */}
          {step === "payment" && (
            <section className="used-card-box">
              <h2 className="used-card-box-title">Manual Off-Platform Payment</h2>
              <div className="used-payment-instruction-card">
                <p>
                  Please pay <strong>₹{plan?.price}</strong> using UPI or Bank Transfer to
                  our official account, then enter your transaction details below.
                </p>
                <div className="used-upi-details-box">
                  <div>
                    <small>UPI ID:</small> <strong>mastermart@upi</strong>
                  </div>
                  <div>
                    <small>GPay / PhonePe / WhatsApp Pay:</small>{" "}
                    <strong>+91 7293838383</strong>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePaymentSubmit} className="used-payment-form">
                <div className="used-input-group">
                  <label className="used-input-label">
                    Transaction ID / UTR Number <span className="used-required">*</span>
                  </label>
                  <input
                    required
                    className="used-text-input"
                    placeholder="e.g. UPI/329482910482/PAY"
                    value={payment.transaction_id}
                    onChange={(e) =>
                      setPayment((prev) => ({ ...prev, transaction_id: e.target.value }))
                    }
                  />
                </div>

                <div className="used-input-group">
                  <label className="used-input-label">
                    Payment Proof Image (Screenshot) <span className="used-required">*</span>
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProofUpload}
                    disabled={uploadingProof}
                    className="used-file-input"
                  />
                  {uploadingProof && (
                    <span className="used-muted-text">Uploading proof to S3...</span>
                  )}
                  {payment.proof_url && (
                    <div className="used-proof-preview">
                      <img src={payment.proof_url} alt="Proof preview" />
                      <span className="used-success-text">Proof uploaded successfully</span>
                    </div>
                  )}
                </div>

                <div className="used-form-actions-bar">
                  <button
                    className="used-primary-btn"
                    disabled={
                      uploadingProof ||
                      !payment.transaction_id ||
                      !payment.proof_url ||
                      createPayment.isLoading
                    }
                  >
                    {createPayment.isLoading ? "Submitting..." : "Submit Payment Proof"}
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* STEP 4: VERIFICATION PENDING */}
          {step === "pending" && (
            <section className="used-card-box used-center-text">
              <CheckCircleIcon style={{ fontSize: 54, color: "#0f766e", marginBottom: 12 }} />
              <h2>Payment Verification Submitted</h2>
              <p className="used-muted-text">
                Your payment request has been received. Our admin team will verify the payment
                details and activate your subscription shortly.
              </p>

              {payment.whatsapp_url && (
                <a
                  className="used-btn-whatsapp"
                  href={payment.whatsapp_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginTop: 16 }}
                >
                  <WhatsAppIcon style={{ fontSize: 20, marginRight: 6 }} />
                  Notify Admin on WhatsApp
                </a>
              )}

              <div style={{ marginTop: 20 }}>
                <Link className="used-secondary-btn" to="/used-items/my-subscriptions">
                  View My Subscriptions
                </Link>
              </div>
            </section>
          )}
        </main>
        <BottomNav />
      </div>
    </AuthGate>
  );
}

/* ── ADD / EDIT ITEM FORM (4-STEP WIZARD) ── */
function ItemForm({ edit = false }) {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { id } = useParams();

  const [step, setStep] = useState("details"); // category -> details -> review -> subscription -> pending
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const existingItemQuery = useUsedItemsQuery.myItem(id);
  const initialData = existingItemQuery.data?.data || existingItemQuery.data;

  const categoriesQuery = useUsedItemsQuery.categories({ limit: 100, offset: 0 });
  const categoriesList = extractList(categoriesQuery).filter((c) => c.is_active !== false);

  const initialCatId = searchParams.get("category") || initialData?.category?.id || "";
  const [selectedCategoryId, setSelectedCategoryId] = useState(initialCatId);

  // For new items with no pre-supplied category, start on the category-selection step
  React.useEffect(() => {
    if (!edit && !initialCatId) {
      setStep("category");
    }
  }, []); // run once on mount

  useEffect(() => {
    if (initialData?.category?.id) {
      setSelectedCategoryId(String(initialData.category.id));
    } else if (searchParams.get("category")) {
      setSelectedCategoryId(String(searchParams.get("category")));
    }
  }, [initialData, location.search]);

  const categoryQuery = useUsedItemsQuery.category(selectedCategoryId);
  const categoryObj = categoryQuery.data?.data || categoryQuery.data;
  const activeCategory = categoryObj;
  const fields = activeCategory?.fields || [];

  const createItemMutation = useUsedItemsQuery.createItem();
  const updateItemMutation = useUsedItemsQuery.updateItem();
  const deleteItemMutation = useUsedItemsQuery.deleteItem();
  const addItemImageMutation = useUsedItemsQuery.addItemImage();
  const deleteItemImageMutation = useUsedItemsQuery.deleteItemImage();
  const subscriptionsQuery = useUsedItemsQuery.subscriptions();
  const plansQuery = useUsedItemsQuery.plans();
  const createSubscriptionMutation = useUsedItemsQuery.createSubscription();
  const createPaymentMutation = useUsedItemsQuery.createPayment();

  const subscriptionsList = extractList(subscriptionsQuery);
  const plansList = extractList(plansQuery).filter((p) => p.is_active !== false);

  const activeSub = subscriptionsList.find((s) => {
    const st = String(s.status || "").toLowerCase();
    if (st !== "active") return false;
    if (s.plan?.is_active === false) return false;
    if (s.end_date && new Date(s.end_date) <= new Date()) return false;
    const ptype = String(s.plan_type_snapshot || s.plan?.plan_type || "").toLowerCase();
    return ptype === "item" || ptype === "time";
  });

  const hasUsedFreePlan = subscriptionsList.some(
    (s) => Number(s.plan?.price ?? 1) === 0
  );

  const [values, setValues] = useState({
    title: "",
    description: "",
    price: "",
    location: "",
    latitude: "",
    longitude: "",
    seller_mobile: "",
    field_values: {},
    image_urls: [],
  });
  const [imageEntries, setImageEntries] = useState([]);
  const [originalImageEntries, setOriginalImageEntries] = useState([]);

  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [transactionId, setTransactionId] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);
  const [paymentNotice, setPaymentNotice] = useState("");
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [detectingLocation, setDetectingLocation] = useState(false);

  const handlePlaceSelect = (place) => {
    if (!place?.geometry?.location) {
      if (place?.name) {
        setValues((prev) => ({ ...prev, location: place.name }));
      }
      return;
    }
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const locName = place.formatted_address || place.name || "";
    setValues((prev) => ({
      ...prev,
      location: locName,
      latitude: String(lat),
      longitude: String(lng),
    }));
  };

  const handleDetectCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setValues((prev) => ({
          ...prev,
          latitude: String(lat),
          longitude: String(lng),
        }));
        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const geoData = await res.json();
          if (geoData?.results?.[0]?.formatted_address) {
            setValues((prev) => ({
              ...prev,
              location: geoData.results[0].formatted_address,
            }));
          }
        } catch (_) {
          // fallback
        }
        setDetectingLocation(false);
      },
      (err) => {
        setDetectingLocation(false);
        alert("Unable to retrieve GPS location: " + err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  useEffect(() => {
    if (initialData) {
      const loadedImages = (initialData.images || []).map((img) => ({
        id: img.id,
        image: img.image,
      }));
      setImageEntries(loadedImages);
      setOriginalImageEntries(loadedImages);
      setValues({
        title: initialData.title || "",
        description: initialData.description || "",
        price: initialData.price || "",
        location: initialData.location || "",
        latitude: initialData.latitude || "",
        longitude: initialData.longitude || "",
        seller_mobile: initialData.seller_mobile || "",
        field_values: initialData.field_values || {},
        image_urls: (initialData.images || []).map((img) => img.image),
      });
    }
  }, [initialData]);

  const handleCategorySelect = (e) => {
    const newCatId = e.target.value;
    if (
      Object.keys(values.field_values).length > 0 &&
      !window.confirm("Changing category will reset entered specifications. Continue?")
    ) {
      return;
    }
    setSelectedCategoryId(newCatId);
    setValues((prev) => ({ ...prev, field_values: {} }));
  };

  const setFieldValue = (key, val) => {
    setValues((prev) => ({
      ...prev,
      field_values: { ...prev.field_values, [key]: val },
    }));
  };

  const handleMultiImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingImages(true);
    setErrorMsg("");

    try {
      const uploadedUrls = [];
      for (const file of files) {
        const url = await uploadFileToS3(file);
        uploadedUrls.push(url);
      }

      setValues((prev) => ({
        ...prev,
        image_urls: [...prev.image_urls, ...uploadedUrls],
      }));
    } catch (err) {
      setErrorMsg("Image upload failed: " + err.message);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleAddEditImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setUploadingImages(true);
    setErrorMsg("");
    try {
      const uploadedEntries = [];
      for (const file of files) {
        uploadedEntries.push({ id: null, image: await uploadFileToS3(file) });
      }
      setImageEntries((prev) => [...prev, ...uploadedEntries]);
    } catch (err) {
      setErrorMsg("Image upload failed: " + err.message);
    } finally {
      setUploadingImages(false);
      e.target.value = "";
    }
  };

  const handleReplaceEditImage = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImages(true);
    setErrorMsg("");
    try {
      const image = await uploadFileToS3(file);
      setImageEntries((prev) => prev.map((entry, entryIndex) => (
        entryIndex === index ? { ...entry, image } : entry
      )));
    } catch (err) {
      setErrorMsg("Image replacement failed: " + err.message);
    } finally {
      setUploadingImages(false);
      e.target.value = "";
    }
  };

  const removeImage = (idxToRemove) => {
    setValues((prev) => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, idx) => idx !== idxToRemove),
    }));
  };

  const removeEditImage = (idxToRemove) => {
    if (!window.confirm("Are you sure you want to remove this image?")) return;
    setImageEntries((prev) => prev.filter((_, index) => index !== idxToRemove));
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProof(true);
    setErrorMsg("");
    try {
      const url = await uploadFileToS3(file);
      setProofUrl(url);
    } catch (err) {
      setErrorMsg("Failed to upload proof image: " + err.message);
    } finally {
      setUploadingProof(false);
    }
  };

  /* Step 1 Validation -> Move to Step 2 Review */
  const handleContinueToReview = (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!selectedCategoryId) {
      setErrorMsg("Please select an item category.");
      return;
    }

    if (!values.title.trim()) {
      setErrorMsg("Please enter an item title.");
      return;
    }

    if (!values.description.trim()) {
      setErrorMsg("Please enter an item description.");
      return;
    }

    if (values.price === "" || Number(values.price) < 0) {
      setErrorMsg("Price must be a non-negative number.");
      return;
    }

    for (const field of fields) {
      if (field.is_required) {
        const val = values.field_values[field.key];
        if (val === undefined || val === null || val === "") {
          setErrorMsg(`Please fill in required category specification: ${labelFor(field)}`);
          return;
        }
      }
    }

    if (!values.seller_mobile.trim()) {
      setErrorMsg("Please enter your contact mobile number.");
      return;
    }

    if (!edit && values.image_urls.length === 0) {
      setErrorMsg("Please upload at least one photo of your item.");
      return;
    }

    if (edit) {
      handleUpdateItem();
      return;
    }

    setStep("review");
  };

  const handleUpdateItem = async () => {
    try {
      const persistedImages = [];
      for (const original of originalImageEntries) {
        const current = imageEntries.find((entry) => entry.id === original.id);
        if (!current) {
          await deleteItemImageMutation.mutateAsync({ itemId: id, id: original.id });
          continue;
        }

        if (current.image !== original.image) {
          await deleteItemImageMutation.mutateAsync({ itemId: id, id: original.id });
          const replacementResponse = await addItemImageMutation.mutateAsync({ id, image: current.image });
          const replacement = replacementResponse?.data || replacementResponse;
          persistedImages.push({ id: replacement?.id, image: current.image });
        } else {
          persistedImages.push(current);
        }
      }

      for (const entry of imageEntries.filter((image) => !image.id)) {
        const addedResponse = await addItemImageMutation.mutateAsync({ id, image: entry.image });
        const addedImage = addedResponse?.data || addedResponse;
        persistedImages.push({ id: addedImage?.id, image: entry.image });
      }

      await updateItemMutation.mutateAsync({
        id,
        title: values.title,
        description: values.description,
        price: Number(values.price),
        field_values: values.field_values,
        location: values.location,
        latitude: values.latitude ? Number(values.latitude) : null,
        longitude: values.longitude ? Number(values.longitude) : null,
        seller_mobile: values.seller_mobile,
      });
      setImageEntries(persistedImages);
      setOriginalImageEntries(persistedImages);
      setSuccessMsg("Updated successfully.");
      window.setTimeout(() => history.push("/used-items/my-items"), 700);
    } catch (err) {
      setErrorMsg("Update failed: " + apiError(err));
    }
  };

  const handleDeleteItem = async () => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;

    setErrorMsg("");
    setSuccessMsg("");
    try {
      await deleteItemMutation.mutateAsync(id);
      setSuccessMsg("Deleted successfully.");
      window.setTimeout(() => history.push("/used-items/my-items"), 700);
    } catch (err) {
      setErrorMsg("Delete failed: " + apiError(err));
    }
  };

  /* Publish Item using Active Subscription or Free Plan */
  const handlePublishItem = async () => {
    setErrorMsg("");

    try {
      const payload = {
        title: values.title,
        description: values.description,
        price: Number(values.price),
        field_values: values.field_values,
        location: values.location,
        latitude: values.latitude ? Number(values.latitude) : null,
        longitude: values.longitude ? Number(values.longitude) : null,
        seller_mobile: values.seller_mobile,
      };

      if (!edit) {
        payload.category_id = Number(selectedCategoryId);
        payload.image_urls = values.image_urls;
      }

      // If user selected a free plan, subscribe first
      if (!activeSub && selectedPlan && Number(selectedPlan.price) === 0) {
        await createSubscriptionMutation.mutateAsync({ plan_id: selectedPlan.id });
      }

      const result = edit
        ? await updateItemMutation.mutateAsync({ id, ...payload })
        : await createItemMutation.mutateAsync(payload);

      const newItemId = result?.data?.id || result?.id;
      history.replace(newItemId ? `/used-items/items/${newItemId}` : "/used-items/my-items");
    } catch (err) {
      const msg = apiError(err);
      if (!activeSub && msg && msg.includes("No eligible")) {
        setStep("subscription");
      }
      setErrorMsg(msg);
    }
  };

  /* Submit Paid Plan Payment and Publish Item */
  const handleSubmitPaidPlan = async (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      setErrorMsg("Please select a subscription plan.");
      return;
    }
    if (!transactionId || !proofUrl) {
      setErrorMsg("Please provide transaction ID and upload payment proof.");
      return;
    }

    setErrorMsg("");
    try {
      const subRes = await createSubscriptionMutation.mutateAsync({ plan_id: selectedPlan.id });
      const subObj = subRes?.data || subRes;

      const payRes = await createPaymentMutation.mutateAsync({
        subscription_id: subObj.id,
        transaction_id: transactionId,
        proof_url: proofUrl,
      });

      const payData = payRes?.data || payRes;
      const waUrl =
        payData?.whatsapp_url ||
        `https://wa.me/917293838383?text=${encodeURIComponent(
          `Seller subscription request:
Plan: ${selectedPlan.name}
Amount: ₹${selectedPlan.price}
Transaction ID: ${transactionId}
Please verify my payment.`
        )}`;

      setWhatsappUrl(waUrl);
      setPaymentNotice(
        "Payment verification submitted successfully! Admin will verify and approve your subscription within 1 working day. Once approved, you can publish your listing directly."
      );
      setStep("pending");
    } catch (err) {
      setErrorMsg(apiError(err));
    }
  };

  return (
    <AuthGate>
      <div className="used-market-page">
        <Header title={edit ? "Edit Listing" : (step === "category" ? "Sell Your Item" : activeCategory?.name && values.title ? `${activeCategory.name} — ${values.title}` : activeCategory?.name || values.title || "Sell Your Item")} />

        <main className="used-market-main-content">
          {/* STEPPER BAR */}
          {!edit && (
            <div className="used-wizard-stepper">
              <div className={`used-step-item ${step === "category" ? "active" : ""}`}>
                1. Category
              </div>
              <div className={`used-step-item ${step === "details" ? "active" : ""}`}>
                2. Item Details
              </div>
              <div className={`used-step-item ${step === "review" ? "active" : ""}`}>
                3. Review
              </div>
              {!activeSub && (
                <div className={`used-step-item ${step === "subscription" || step === "pending" ? "active" : ""}`}>
                  4. Subscription & Payment
                </div>
              )}
            </div>
          )}

          {errorMsg && <Message error>{errorMsg}</Message>}
          {successMsg && <Message>{successMsg}</Message>}

          {/* ── STEP 0: CATEGORY SELECTION ── */}
          {step === "category" && !edit && (
            <section className="used-card-box">
              <h2 className="used-card-box-title">Select a Category</h2>
              {categoriesQuery.isLoading ? (
                <Message>Loading categories…</Message>
              ) : (
                <>
                  <p className="used-muted-text" style={{ marginBottom: 14 }}>
                    {categoriesList.length} categor{categoriesList.length === 1 ? "y" : "ies"} available
                  </p>
                  <div className="usf-cat-pick-grid">
                    {categoriesList.map((cat) => (
                      <div
                        key={cat.id}
                        className={`usf-cat-pick-card ${String(cat.id) === String(selectedCategoryId) ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedCategoryId(String(cat.id));
                          setValues((prev) => ({ ...prev, field_values: {} }));
                          setStep("details");
                        }}
                      >
                        <div className="usf-cat-pick-icon">
                          {cat.image ? (
                            <img
                              src={cat.image}
                              alt={cat.name}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <span>📦</span>
                          )}
                        </div>
                        <span className="usf-cat-pick-name">{cat.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

          {/* ── STEP 1: ITEM DETAILS ── */}
          {(step === "details" || edit) && (
            <form className="usf-form" onSubmit={handleContinueToReview}>
              {/* Subheader shows selected category */}
              <div className="usf-subheader">
                {activeCategory?.name ? `${activeCategory.name} — Item Details` : "Item Details"}
              </div>

              {/* 2. TITLE */}
              <div className="usf-field-row">
                <div className="usf-field-label">Title <span className="used-required">*</span></div>
                <div className="usf-field-input">
                  <input
                    required
                    className="usf-input"
                    placeholder="e.g. Honda Activa 6G 2023"
                    value={values.title}
                    onChange={(e) => setValues({ ...values, title: e.target.value })}
                  />
                </div>
              </div>

              {/* 3. DESCRIPTION */}
              <div className="usf-field-row usf-field-row--tall">
                <div className="usf-field-label">Description <span className="used-required">*</span></div>
                <div className="usf-field-input">
                  <textarea
                    required
                    className="usf-textarea"
                    placeholder="Condition, ownership, specs..."
                    value={values.description}
                    onChange={(e) => setValues({ ...values, description: e.target.value })}
                  />
                </div>
              </div>

              {/* 4. PRICE */}
              <div className="usf-field-row">
                <div className="usf-field-label">Price ₹ <span className="used-required">*</span></div>
                <div className="usf-field-input">
                  <input
                    required
                    type="number"
                    min="0"
                    step="0.01"
                    className="usf-input"
                    placeholder="0.00"
                    value={values.price}
                    onChange={(e) => setValues({ ...values, price: e.target.value })}
                  />
                </div>
              </div>

              {/* 5. LOCATION */}
              <div className="usf-field-row">
                <div className="usf-field-label">Location / City</div>
                <div className="usf-field-input usf-field-input--loc">
                  <ReactGoogleAutocomplete
                    apiKey={GOOGLE_MAPS_API_KEY}
                    options={{
                      types: ["neighborhood", "locality", "sublocality", "postal_code", "point_of_interest"],
                      componentRestrictions: { country: "in" },
                      fields: ["address_components", "geometry", "name", "formatted_address", "place_id"],
                    }}
                    onPlaceSelected={handlePlaceSelect}
                    placeholder="Search city..."
                    defaultValue={values.location}
                    className="usf-input"
                    onChange={(e) => setValues((prev) => ({ ...prev, location: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="usf-loc-btn"
                    onClick={handleDetectCurrentLocation}
                    disabled={detectingLocation}
                  >
                    <MyLocationIcon style={{ fontSize: 14, marginRight: 4 }} />
                    {detectingLocation ? "Detecting..." : "Use Current Location"}
                  </button>
                </div>
              </div>

              {/* 6. MOBILE */}
              <div className="usf-field-row">
                <div className="usf-field-label">Mobile Number <span className="used-required">*</span></div>
                <div className="usf-field-input">
                  <input
                    required
                    type="tel"
                    className="usf-input"
                    placeholder="10-digit number"
                    value={values.seller_mobile}
                    onChange={(e) => setValues({ ...values, seller_mobile: e.target.value })}
                  />
                </div>
              </div>

              {/* 7. DYNAMIC CATEGORY FIELDS */}
              {selectedCategoryId && (
                categoryQuery.isLoading ? (
                  <div className="usf-field-row"><div className="usf-field-label">Fields</div><div className="usf-field-input"><span className="used-muted-text">Loading...</span></div></div>
                ) : fields.length > 0 ? (
                  fields.map((field) => {
                    const isReq = field.is_required;
                    const currentVal = values.field_values[field.key] ?? "";
                    return (
                      <div key={field.id || field.key} className="usf-field-row">
                        <div className="usf-field-label">
                          {labelFor(field)}{isReq && <span className="used-required"> *</span>}
                        </div>
                        <div className="usf-field-input">
                          {field.field_type === "dropdown" ? (
                            <CustomDropdown
                              required={isReq}
                              className="usd-usf-field"
                              value={currentVal}
                              onChange={(val) => setFieldValue(field.key, val)}
                              options={field.options || []}
                              placeholder="Select"
                            />
                          ) : field.field_type === "boolean" ? (
                            <div className="used-checkbox-row">
                              <input
                                type="checkbox"
                                id={`chk_${field.key}`}
                                checked={!!values.field_values[field.key]}
                                onChange={(e) => setFieldValue(field.key, e.target.checked)}
                              />
                              <label htmlFor={`chk_${field.key}`}>{labelFor(field)}</label>
                            </div>
                          ) : (
                            <input
                              required={isReq}
                              className="usf-input"
                              type={field.field_type === "number" || field.field_type === "decimal" ? "number" : field.field_type === "date" ? "date" : "text"}
                              placeholder={`Enter ${labelFor(field)}`}
                              value={currentVal}
                              onChange={(e) => setFieldValue(field.key, e.target.value)}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : null
              )}

              {/* 8. PHOTOS */}
              {!edit && (
                <div className="usf-field-row usf-field-row--tall">
                  <div className="usf-field-label">Images <span className="used-required">*</span></div>
                  <div className="usf-field-input">
                    <div className="used-file-upload-dropzone">
                      <CloudUploadIcon style={{ fontSize: 30, color: "#0f766e", marginBottom: 4 }} />
                      <p>+ Add Photos</p>
                      <input type="file" accept="image/*" multiple onChange={handleMultiImageUpload} disabled={uploadingImages} />
                    </div>
                    {uploadingImages && <span className="used-muted-text">Uploading...</span>}
                    {values.image_urls.length > 0 && (
                      <div className="used-uploaded-thumbnails-grid">
                        {values.image_urls.map((url, idx) => (
                          <div key={idx} className="used-uploaded-thumb-box">
                            <img src={url} alt={`Upload ${idx + 1}`} />
                            <button type="button" className="used-remove-thumb-btn" onClick={() => removeImage(idx)} aria-label="Remove photo">×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {edit && (
                <section className="usf-image-management" aria-labelledby="usf-image-management-title">
                  <div className="usf-image-management-header">
                    <div>
                      <h2 id="usf-image-management-title">Listing Images</h2>
                      <p>Replace, remove, or add images for this listing.</p>
                    </div>
                    <label className="usf-image-add-btn">
                      Add Image
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAddEditImages}
                        disabled={uploadingImages || addItemImageMutation.isLoading || deleteItemImageMutation.isLoading}
                      />
                    </label>
                  </div>

                  {existingItemQuery.isLoading ? (
                    <div className="usf-image-status">Loading images...</div>
                  ) : existingItemQuery.isError ? (
                    <div className="usf-image-status usf-image-status--error">Unable to load listing images. Please refresh and try again.</div>
                  ) : imageEntries.length === 0 ? (
                    <div className="usf-image-status">No images registered for this listing.</div>
                  ) : (
                    <div className="usf-image-grid">
                      {imageEntries.map((entry, index) => (
                        <div className="usf-image-card" key={`${entry.id || "new"}-${index}`}>
                          <img src={entry.image} alt={`Listing image ${index + 1}`} />
                          <div className="usf-image-card-actions">
                            <label className="usf-image-control">
                              Replace
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleReplaceEditImage(index, e)}
                                disabled={uploadingImages || addItemImageMutation.isLoading || deleteItemImageMutation.isLoading}
                              />
                            </label>
                            <button type="button" className="usf-image-control usf-image-remove" onClick={() => removeEditImage(index)}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {uploadingImages && <div className="usf-image-status">Uploading image...</div>}
                </section>
              )}

              {/* BACK / SAVE / NEXT — Three distinct action buttons below Add Photos */}
              <div className={`usf-action-trio ${edit ? "usf-action-trio--edit" : ""}`}>
                <button
                  type="button"
                  className="usf-trio-btn usf-trio-back"
                  onClick={() => edit ? history.push("/used-items") : setStep("category")}
                  aria-label="Go back"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><polyline points="15 18 9 12 15 6"/></svg>
                  Back
                </button>
                <button
                  type="submit"
                  className="usf-trio-btn usf-trio-save"
                  disabled={uploadingImages || createItemMutation.isLoading || updateItemMutation.isLoading || deleteItemMutation.isLoading || addItemImageMutation.isLoading || deleteItemImageMutation.isLoading}
                  aria-label={edit ? "Save changes" : "Save and review"}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                  {edit
                    ? (updateItemMutation.isLoading ? "Saving…" : "Save")
                    : (uploadingImages ? "Uploading…" : "Save")}
                </button>
                <button
                  type="submit"
                  className="usf-trio-btn usf-trio-next"
                  disabled={uploadingImages || createItemMutation.isLoading || updateItemMutation.isLoading || deleteItemMutation.isLoading || addItemImageMutation.isLoading || deleteItemImageMutation.isLoading}
                  aria-label={edit ? "Save changes" : "Next step"}
                >
                  {edit ? (updateItemMutation.isLoading ? "Updating…" : "Update") : "Next"}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
                {edit && (
                  <button
                    type="button"
                    className="usf-trio-btn usf-trio-delete"
                    onClick={handleDeleteItem}
                    disabled={uploadingImages || createItemMutation.isLoading || updateItemMutation.isLoading || deleteItemMutation.isLoading || addItemImageMutation.isLoading || deleteItemImageMutation.isLoading}
                    aria-label="Delete listing"
                  >
                    {deleteItemMutation.isLoading ? "Deleting…" : "Delete"}
                  </button>
                )}
              </div>

              {/* FIXED BOTTOM BAR (hidden on mobile when trio is visible, kept for desktop) */}
              <div className="usf-bottom-bar usf-bottom-bar--hidden-mobile">
                <button
                  type="button"
                  className="usf-back-btn"
                  onClick={() => edit ? history.push("/used-items") : setStep("category")}
                >
                  ◀
                </button>
                <button
                  type="submit"
                  className="usf-next-btn"
                  disabled={uploadingImages || (edit && (createItemMutation.isLoading || updateItemMutation.isLoading))}
                >
                  {edit
                    ? (updateItemMutation.isLoading ? "Saving…" : "💾 Save Changes")
                    : (uploadingImages ? "Uploading…" : "Next — Review ›")}
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 2: FINAL REVIEW ── */}
          {step === "review" && !edit && (
            <section className="used-card-box">
              <h2 className="used-card-box-title">Review Item Details</h2>
              <p className="used-muted-text">
                Please double check your item information before choosing subscription.
              </p>

              {/* Photos Gallery Review */}
              {values.image_urls.length > 0 && (
                <div className="used-review-photos-row">
                  {values.image_urls.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Review ${idx}`}
                    />
                  ))}
                </div>
              )}

              {/* Basic Specs Table Review */}
              <div className="used-specs-grid">
                <div className="used-spec-item">
                  <span className="used-spec-label">Category</span>
                  <span className="used-spec-value">{activeCategory?.name || "Selected Category"}</span>
                </div>
                <div className="used-spec-item">
                  <span className="used-spec-label">Title</span>
                  <span className="used-spec-value">{values.title}</span>
                </div>
                <div className="used-spec-item">
                  <span className="used-spec-label">Price</span>
                  <span className="used-spec-value">₹{Number(values.price).toLocaleString("en-IN")}</span>
                </div>
                <div className="used-spec-item">
                  <span className="used-spec-label">Location</span>
                  <span className="used-spec-value">{values.location || "Not specified"}</span>
                </div>
                <div className="used-spec-item">
                  <span className="used-spec-label">Mobile Number</span>
                  <span className="used-spec-value">{values.seller_mobile}</span>
                </div>
              </div>

              {/* Description */}
              <div className="used-review-desc-wrap">
                <strong className="used-review-desc-label">Description:</strong>
                <p className="used-review-desc-val">{values.description}</p>
              </div>

              {/* Dynamic Category Specs Review */}
              {fields.length > 0 && (
                <div className="used-review-specs-wrap">
                  <h4 className="used-review-specs-title">Category Specifications</h4>
                  <DynamicFields
                    fields={fields}
                    values={values.field_values}
                    readOnly
                  />
                </div>
              )}

              {/* STEP 2 ACTION BUTTONS */}
              <div className="used-form-actions-bar">
                <button
                  type="button"
                  className="used-secondary-btn"
                  onClick={() => setStep("details")}
                >
                  ‹ Back & Edit
                </button>
                <button
                  type="button"
                  className="used-primary-btn"
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                  disabled={createItemMutation.isLoading || subscriptionsQuery.isLoading}
                  onClick={
                    activeSub || subscriptionsQuery.isError
                      ? handlePublishItem
                      : () => setStep("subscription")
                  }
                >
                  {subscriptionsQuery.isLoading
                    ? "Checking subscription..."
                    : activeSub || subscriptionsQuery.isError
                    ? (createItemMutation.isLoading
                        ? "Publishing…"
                        : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg> Save &amp; Publish</>)
                    : "Continue to Subscription & Payment ›"}
                </button>
              </div>

              {/* Contact preview — WhatsApp & Call using entered mobile */}
              {values.seller_mobile && (
                <div style={{ marginTop: 16, padding: "12px 14px", background: "var(--used-bg)", borderRadius: 10, border: "1px solid var(--used-border)" }}>
                  <div style={{ fontSize: 12, color: "var(--used-muted)", marginBottom: 8, fontWeight: 600 }}>
                    Buyer contact will reach you on:
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <a
                      href={`tel:${values.seller_mobile.replace(/\D/g, "")}`}
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 42, borderRadius: 8, background: "#1976d2", color: "#fff", textDecoration: "none", fontSize: 14, fontWeight: 700 }}
                    >
                      <PhoneIcon style={{ fontSize: 18 }} /> Call
                    </a>
                    <a
                      href={`https://wa.me/${values.seller_mobile.replace(/\D/g, "").replace(/^0/, "91")}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: 42, borderRadius: 8, background: "#25d366", color: "#fff", textDecoration: "none", fontSize: 14, fontWeight: 700 }}
                    >
                      <WhatsAppIcon style={{ fontSize: 18 }} /> WhatsApp
                    </a>
                  </div>
                </div>
              )}

              {/* ── SAVE & CONTINUE — fixed bottom bar, full width, always visible ── */}
              <div className="usf-bottom-bar">
                <button
                  type="button"
                  className="usf-back-btn"
                  onClick={() => setStep("details")}
                  aria-label="Back to edit"
                >
                  ◀
                </button>
                <button
                  type="button"
                  className="usf-next-btn"
                  style={{ background: createItemMutation.isLoading ? "#4e9d91" : "#0f766e", letterSpacing: "0.6px" }}
                  disabled={createItemMutation.isLoading || subscriptionsQuery.isLoading}
                  onClick={
                    activeSub || subscriptionsQuery.isError
                      ? handlePublishItem
                      : () => setStep("subscription")
                  }
                >
                  {subscriptionsQuery.isLoading
                    ? "Checking…"
                    : createItemMutation.isLoading
                    ? "Saving…"
                    : activeSub || subscriptionsQuery.isError
                    ? "SAVE & CONTINUE"
                    : "SAVE & CONTINUE ›"}
                </button>
              </div>
            </section>
          )}

          {/* ── STEP 3: SUBSCRIPTION & PAYMENT ── */}
          {step === "subscription" && !edit && !activeSub && (
            <section className="used-card-box">
              <h2 className="used-card-box-title">Subscription & Payment</h2>

              {/* ACTIVE SUBSCRIPTION AVAILABLE */}
              {activeSub ? (
                <div>
                  <div className="used-payment-instruction-card">
                    <div className="used-payment-header-row">
                      <h3>Active Subscription</h3>
                      <span className="used-status-pill status-published">Active</span>
                    </div>
                    <div className="used-record-details">
                      {activeSub.plan_type_snapshot === "item" ? (
                        <>
                          <div>Plan: <strong>{activeSub.plan?.name || "Item Plan"}</strong></div>
                          <div>Used: <strong>{activeSub.items_used} / {activeSub.item_limit_snapshot}</strong></div>
                          <div>Remaining: <strong>{Math.max(0, (activeSub.item_limit_snapshot || 0) - activeSub.items_used)}</strong></div>
                        </>
                      ) : (
                        <>
                          <div>Plan: <strong>{activeSub.plan?.name || "1 Month Unlimited"}</strong></div>
                          <div>Valid Until: <strong>{activeSub.end_date ? new Date(activeSub.end_date).toLocaleDateString() : "Active"}</strong></div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="used-form-actions-bar">
                    <button
                      type="button"
                      className="used-secondary-btn"
                      onClick={() => setStep("review")}
                    >
                      ‹ Back to Review
                    </button>
                    <button
                      type="button"
                      className="used-primary-btn"
                      disabled={createItemMutation.isLoading}
                      onClick={handlePublishItem}
                    >
                      {createItemMutation.isLoading ? "Publishing..." : "Publish Item"}
                    </button>
                  </div>
                </div>
              ) : (
                /* NO ACTIVE SUBSCRIPTION -> PLAN SELECTION & PAYMENT */
                <div>
                  {!selectedPlan ? (
                    <div>
                      <p className="used-muted-text">
                        You need a valid subscription plan to publish items. Select a plan below:
                      </p>

                      {plansQuery.isLoading ? (
                        <Message>Loading subscription plans...</Message>
                      ) : plansList.length === 0 ? (
                        <Message>No subscription plans available from backend.</Message>
                      ) : (
                        <div className="used-plans-cards-grid">
                          {plansList.map((p) => {
                            const isFree = Number(p.price) === 0;
                            const isFreeDisabled = isFree && hasUsedFreePlan;
                            const isItemPlan = p.plan_type === "item";
                            return (
                              <div
                                key={p.id}
                                className={`used-plan-card ${isFreeDisabled ? "disabled" : ""}`}
                                onClick={() => {
                                  if (!isFreeDisabled) setSelectedPlan(p);
                                }}
                                style={isFreeDisabled ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                              >
                                <div className="used-plan-card-header">
                                  <h4>{p.name}</h4>
                                  <span className={`used-plan-price-badge ${isFree ? "free" : ""}`}>
                                    {isFreeDisabled
                                      ? "ALREADY USED"
                                      : isFree
                                      ? "FREE"
                                      : `₹${Number(p.price).toLocaleString("en-IN")}`}
                                  </span>
                                </div>
                                <div className="used-plan-card-details">
                                  {isItemPlan ? (
                                    <div className="used-plan-feature">
                                      <CheckCircleIcon style={{ fontSize: 16, color: "#0f766e" }} />
                                      <span>{p.item_limit} Items Listing Quota</span>
                                    </div>
                                  ) : (
                                    <div className="used-plan-feature">
                                      <CheckCircleIcon style={{ fontSize: 16, color: "#0f766e" }} />
                                      <span>Unlimited Items for {p.duration_days} Days</span>
                                    </div>
                                  )}
                                  {isFreeDisabled && (
                                    <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 4 }}>
                                      One-time free plan already claimed
                                    </div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className={isFreeDisabled ? "used-secondary-btn" : "used-primary-btn"}
                                  disabled={isFreeDisabled}
                                >
                                  {isFreeDisabled ? "Already Claimed" : "Select Plan"}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="used-form-actions-bar">
                        <button
                          type="button"
                          className="used-secondary-btn"
                          onClick={() => setStep("review")}
                        >
                          ‹ Back to Review
                        </button>
                      </div>
                    </div>
                  ) : Number(selectedPlan.price) === 0 ? (
                    /* STEP 4: FREE PLAN SELECTED */
                    <div>
                      <div className="used-payment-instruction-card">
                        <h3>Selected: {selectedPlan.name}</h3>
                        <p>Amount: <strong>₹0 (Free Plan)</strong></p>
                      </div>

                      <div className="used-form-actions-bar">
                        <button
                          type="button"
                          className="used-secondary-btn"
                          onClick={() => setSelectedPlan(null)}
                        >
                          ‹ Change Plan
                        </button>
                        <button
                          type="button"
                          className="used-primary-btn"
                          disabled={createSubscriptionMutation.isLoading || createItemMutation.isLoading}
                          onClick={handlePublishItem}
                        >
                          {createSubscriptionMutation.isLoading || createItemMutation.isLoading
                            ? "Publishing..."
                            : "Publish Item"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* STEP 5: PAID PLAN SELECTED -> MANUAL PAYMENT FORM */
                    <div>
                      <div className="used-payment-instruction-card">
                        <div className="used-payment-header-row">
                          <h4>Subscription: {selectedPlan.name}</h4>
                          <strong>Total Payable: ₹{Number(selectedPlan.price).toLocaleString("en-IN")}</strong>
                        </div>
                        <p>
                          Please transfer <strong>₹{selectedPlan.price}</strong> using UPI or Bank Transfer to our official account:
                        </p>
                        <div className="used-upi-details-box">
                          <div><small>UPI ID:</small> <strong>mastermart@upi</strong></div>
                          <div><small>GPay / PhonePe / WhatsApp Pay:</small> <strong>+91 7293838383</strong></div>
                        </div>
                      </div>

                      <form onSubmit={handleSubmitPaidPlan} className="used-payment-form">
                        <div className="used-input-group">
                          <label className="used-input-label">
                            Transaction ID / UTR Number <span className="used-required">*</span>
                          </label>
                          <input
                            required
                            className="used-text-input"
                            placeholder="e.g. UPI/123456789099"
                            value={transactionId}
                            onChange={(e) => setTransactionId(e.target.value)}
                          />
                        </div>

                        <div className="used-input-group">
                          <label className="used-input-label">
                            Payment Screenshot Proof <span className="used-required">*</span>
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            className="used-file-input"
                            onChange={handleProofUpload}
                            disabled={uploadingProof}
                          />
                          {uploadingProof && <span className="used-muted-text">Uploading proof to S3...</span>}
                          {proofUrl && (
                            <div className="used-proof-preview">
                              <img src={proofUrl} alt="Proof preview" />
                              <span className="used-success-text">Proof uploaded successfully</span>
                            </div>
                          )}
                        </div>

                        <div className="used-form-actions-bar">
                          <button
                            type="button"
                            className="used-secondary-btn"
                            onClick={() => setSelectedPlan(null)}
                          >
                            ‹ Change Plan
                          </button>
                          <button
                            className="used-primary-btn"
                            disabled={
                              uploadingProof ||
                              !transactionId ||
                              !proofUrl ||
                              createSubscriptionMutation.isLoading ||
                              createPaymentMutation.isLoading
                            }
                          >
                            {createSubscriptionMutation.isLoading || createPaymentMutation.isLoading
                              ? "Submitting..."
                              : "Submit Payment & Publish"}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* ── STEP 4: VERIFICATION PENDING SCREEN ── */}
          {step === "pending" && (
            <section className="used-card-box used-center-text">
              <CheckCircleIcon style={{ fontSize: 54, color: "#0f766e", marginBottom: 12 }} />
              <h2>Payment Verification Submitted</h2>
              <p className="used-muted-text">
                {paymentNotice ||
                  "Your payment verification request has been submitted successfully. Our admin team will verify and approve your subscription within 1 working day."}
              </p>

              {whatsappUrl && (
                <a
                  className="used-btn-whatsapp"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginTop: 16 }}
                >
                  <WhatsAppIcon style={{ fontSize: 20, marginRight: 6 }} />
                  Notify Admin on WhatsApp
                </a>
              )}

              <div className="used-pending-actions-row" style={{ marginTop: 20 }}>
                <Link className="used-secondary-btn" to="/used-items/my-subscriptions">
                  View My Subscriptions
                </Link>
                <Link className="used-primary-btn" to="/used-items">
                  Back to Marketplace
                </Link>
              </div>
            </section>
          )}
        </main>
        <BottomNav />
      </div>
    </AuthGate>
  );
}

/* ── MY ITEMS / SUBSCRIPTIONS / PAYMENTS TABS ── */
function MyList({ type }) {
  const history = useHistory();
  const itemsQuery = useUsedItemsQuery.myItems({ limit: 50, offset: 0 });
  const subscriptionsQuery = useUsedItemsQuery.subscriptions();
  const paymentsQuery = useUsedItemsQuery.payments({});
  const plansQuery = useUsedItemsQuery.plans();

  const deleteItemMutation = useUsedItemsQuery.deleteItem();
  const createSubscriptionMutation = useUsedItemsQuery.createSubscription();
  const createPaymentMutation = useUsedItemsQuery.createPayment();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeSubObj, setActiveSubObj] = useState(null);
  const [paymentStep, setPaymentStep] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [uploadingProof, setUploadingProof] = useState(false);
  const [subNotice, setSubNotice] = useState("");

  const activeQuery =
    type === "items"
      ? itemsQuery
      : type === "subscriptions"
      ? subscriptionsQuery
      : paymentsQuery;

  const data = extractList(activeQuery);

  // Detect if user has an active subscription with remaining capacity
  const activeSubWithCapacity = extractList(subscriptionsQuery).find((s) => {
    if (s.status !== "active") return false;
    if (s.plan?.is_active === false) return false;
    if (s.end_date && new Date(s.end_date) <= new Date()) return false;
    if (s.plan_type_snapshot === "item") {
      return s.items_used < (s.item_limit_snapshot || 0);
    }
    return !s.end_date || new Date(s.end_date) > new Date();
  });

  const hasUsedFreePlan = extractList(subscriptionsQuery).some(
    (s) => Number(s.plan?.price ?? 1) === 0
  );

  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Are you sure you want to delete this listing?")) {
      try {
        await deleteItemMutation.mutateAsync(itemId);
        itemsQuery.refetch();
      } catch (err) {
        alert(apiError(err));
      }
    }
  };

  const handleSelectPlan = async (plan) => {
    setSubNotice("");
    setSelectedPlan(plan);
    try {
      const res = await createSubscriptionMutation.mutateAsync({ plan_id: plan.id });
      const subObj = res?.data || res;
      setActiveSubObj(subObj);

      if (Number(plan.price) === 0) {
        setSubNotice("Free plan activated successfully! You can now publish items.");
        subscriptionsQuery.refetch();
      } else {
        setPaymentStep(true);
      }
    } catch (err) {
      setSubNotice(apiError(err));
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProof(true);
    setSubNotice("");
    try {
      const url = await uploadFileToS3(file);
      setProofUrl(url);
    } catch (err) {
      setSubNotice("Failed to upload proof image: " + err.message);
    } finally {
      setUploadingProof(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!transactionId || !proofUrl) {
      setSubNotice("Please provide transaction ID and upload payment proof.");
      return;
    }
    setSubNotice("");
    try {
      await createPaymentMutation.mutateAsync({
        subscription_id: activeSubObj.id,
        transaction_id: transactionId,
        proof_url: proofUrl,
      });
      setSubNotice("Payment verification submitted successfully! Subscription will be activated after admin verification.");
      setPaymentStep(false);
      setTransactionId("");
      setProofUrl("");
      subscriptionsQuery.refetch();
      paymentsQuery.refetch();
    } catch (err) {
      setSubNotice(apiError(err));
    }
  };

  return (
    <AuthGate>
      <div className="used-market-page">
        <Header
          title={
            type === "items"
              ? "My Listings"
              : type === "subscriptions"
              ? "My Subscriptions"
              : "My Payments"
          }
        />

        <main className="used-market-main-content">
          {/* AVAILABLE SUBSCRIPTION PLANS SECTION FOR /used-items/my-subscriptions */}
          {type === "subscriptions" && (
            <section className="used-card-box">
              {activeSubWithCapacity ? (
                <>
                  <h2 className="used-card-box-title">Active Subscription</h2>
                  <div className="used-payment-instruction-card" style={{ background: "#e8f5e9", border: "1px solid #a5d6a7" }}>
                    <p style={{ margin: 0, fontWeight: 600, color: "#2e7d32" }}>
                      ✅ You already have an active <strong>{activeSubWithCapacity.plan?.name || "subscription"}</strong> plan.
                    </p>
                    {activeSubWithCapacity.plan_type_snapshot === "item" ? (
                      <p style={{ margin: "6px 0 0" }}>
                        Items remaining:{" "}
                        <strong>
                          {(activeSubWithCapacity.item_limit_snapshot || 0) - (activeSubWithCapacity.items_used || 0)}
                        </strong>{" "}
                        of {activeSubWithCapacity.item_limit_snapshot}
                      </p>
                    ) : (
                      activeSubWithCapacity.end_date && (
                        <p style={{ margin: "6px 0 0" }}>
                          Valid until: <strong>{new Date(activeSubWithCapacity.end_date).toLocaleDateString()}</strong>
                        </p>
                      )
                    )}
                    <p style={{ margin: "8px 0 0", fontSize: "0.85em", color: "#555" }}>
                      Go to <strong>Sell Used Item</strong> to publish your listing using this subscription.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="used-card-box-title">Available Subscription Plans</h2>
                  <p className="used-muted-text">
                    Select a plan created by admin to start publishing used items. Free plans activate immediately!
                  </p>

                  {subNotice && (
                    <Message error={subNotice.includes("failed") || subNotice.includes("Error")}>
                      {subNotice}
                    </Message>
                  )}

                  {paymentStep ? (
                    <div className="used-payment-instruction-card">
                      <h3>Manual Off-Platform Payment for {selectedPlan?.name}</h3>
                      <p>
                        Please transfer <strong>₹{selectedPlan?.price}</strong> using UPI or Bank Transfer to our official account:
                      </p>
                      <div className="used-upi-details-box">
                        <div><small>UPI ID:</small> <strong>mastermart@upi</strong></div>
                        <div><small>GPay / PhonePe / WhatsApp Pay:</small> <strong>+91 7293838383</strong></div>
                      </div>
                      <form onSubmit={handlePaymentSubmit} className="used-payment-form">
                        <div className="used-input-group">
                          <label className="used-input-label">
                            Transaction ID / UTR Number <span className="used-required">*</span>
                          </label>
                          <input
                            required
                            className="used-text-input"
                            placeholder="e.g. UPI/123456789099"
                            value={transactionId}
                            onChange={(e) => setTransactionId(e.target.value)}
                          />
                        </div>
                        <div className="used-input-group">
                          <label className="used-input-label">
                            Payment Screenshot Proof <span className="used-required">*</span>
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            className="used-file-input"
                            onChange={handleProofUpload}
                            disabled={uploadingProof}
                          />
                          {uploadingProof && <span className="used-muted-text">Uploading proof to S3...</span>}
                          {proofUrl && (
                            <div className="used-proof-preview">
                              <img src={proofUrl} alt="Proof preview" />
                              <span className="used-success-text">Proof uploaded successfully</span>
                            </div>
                          )}
                        </div>
                        <div className="used-form-actions-bar">
                          <button
                            type="button"
                            className="used-secondary-btn"
                            onClick={() => setPaymentStep(false)}
                          >
                            Cancel
                          </button>
                          <button
                            className="used-primary-btn"
                            disabled={uploadingProof || !transactionId || !proofUrl || createPaymentMutation.isLoading}
                          >
                            {createPaymentMutation.isLoading ? "Submitting..." : "Submit Payment Proof"}
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <>
                      {plansQuery.isLoading ? (
                        <Message>Loading available subscription plans...</Message>
                      ) : plansQuery.isError ? (
                        <Message error>{apiError(plansQuery.error)}</Message>
                      ) : extractList(plansQuery).length === 0 ? (
                        <Message>No subscription plans available at the moment.</Message>
                      ) : (
                        <div className="used-plans-cards-grid">
                          {extractList(plansQuery)
                            .filter((p) => p.is_active !== false)
                            .map((p) => {
                              const isFree = Number(p.price) === 0;
                              const isFreeDisabled = isFree && hasUsedFreePlan;
                              const isItemPlan = p.plan_type === "item";
                              return (
                                <div
                                  key={p.id}
                                  className={`used-plan-card ${isFreeDisabled ? "disabled" : ""}`}
                                  style={isFreeDisabled ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                                >
                                  <div className="used-plan-card-header">
                                    <h4>{p.name}</h4>
                                    <span className={`used-plan-price-badge ${isFree ? "free" : ""}`}>
                                      {isFreeDisabled
                                        ? "ALREADY USED"
                                        : isFree
                                        ? "FREE"
                                        : `₹${Number(p.price).toLocaleString("en-IN")}`}
                                    </span>
                                  </div>
                                  <div className="used-plan-card-details">
                                    {isItemPlan ? (
                                      <div className="used-plan-feature">
                                        <CheckCircleIcon style={{ fontSize: 16, color: "#0f766e" }} />
                                        <span>{p.item_limit} Items Listing Quota</span>
                                      </div>
                                    ) : (
                                      <div className="used-plan-feature">
                                        <CheckCircleIcon style={{ fontSize: 16, color: "#0f766e" }} />
                                        <span>Unlimited Items for {p.duration_days} Days</span>
                                      </div>
                                    )}
                                    {isFreeDisabled && (
                                      <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 4 }}>
                                        One-time free plan already claimed
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    className={isFreeDisabled ? "used-secondary-btn" : "used-primary-btn"}
                                    disabled={createSubscriptionMutation.isLoading || isFreeDisabled}
                                    onClick={() => {
                                      if (!isFreeDisabled) handleSelectPlan(p);
                                    }}
                                  >
                                    {createSubscriptionMutation.isLoading && selectedPlan?.id === p.id
                                      ? "Processing..."
                                      : isFreeDisabled
                                      ? "Already Claimed"
                                      : isFree
                                      ? "Select Free Plan"
                                      : "Subscribe Now"}
                                  </button>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </section>
          )}

          <div className="used-section-header">
            <h3>
              {type === "items"
                ? "Your Used Item Listings"
                : type === "subscriptions"
                ? "My Subscriptions History"
                : "Payment Submission History"}
            </h3>

            {type === "items" && (
              <button
                className="used-primary-btn-sm"
                onClick={() => history.push("/used-items/sell")}
              >
                + Sell Item
              </button>
            )}
          </div>

          {activeQuery.isLoading ? (
            <Message>Loading {type}...</Message>
          ) : !data.length ? (
            <Message>No {type} records found.</Message>
          ) : type === "items" ? (
            <div className="used-my-items-list">
              {data.map((item) => {
                const statusPill =
                  item.status === "published"
                    ? "status-published"
                    : item.status === "pending"
                    ? "status-pending"
                    : "status-expired";
                return (
                  <div key={item.id} className="used-my-item-row">
                    <img
                      src={item.images?.[0]?.image || "/thumbnail.png"}
                      alt=""
                      className="used-my-item-img"
                    />
                    <div className="used-my-item-info">
                      <h4>{item.title}</h4>
                      <div className="used-my-item-meta">
                        <span className={`used-status-pill ${statusPill}`}>
                          {item.status}
                        </span>
                        <strong>₹{Number(item.price).toLocaleString("en-IN")}</strong>
                      </div>
                    </div>
                    <div className="used-my-item-actions">
                      <button
                        className="used-icon-action-btn edit"
                        onClick={() => history.push(`/used-items/items/my/${item.id}`)}
                        title="Edit Listing"
                      >
                        <EditIcon style={{ fontSize: 18 }} />
                      </button>
                      <button
                        className="used-icon-action-btn delete"
                        onClick={() => handleDeleteItem(item.id)}
                        title="Delete Listing"
                      >
                        <DeleteIcon style={{ fontSize: 18 }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : type === "subscriptions" ? (
            <div className="used-records-stack">
              {data.map((sub) => {
                const isItemPlan = sub.plan_type_snapshot === "item";
                return (
                  <div key={sub.id} className="used-record-card">
                    <div className="used-record-card-top">
                      <strong>{sub.plan?.name || "Subscription Plan"}</strong>
                      <span
                        className={`used-status-pill ${
                          sub.status === "active"
                            ? "status-published"
                            : sub.status === "pending"
                            ? "status-pending"
                            : "status-expired"
                        }`}
                      >
                        {sub.status}
                      </span>
                    </div>

                    <div className="used-record-details">
                      {isItemPlan ? (
                        <div>
                          Quota Used: <strong>{sub.items_used}</strong> /{" "}
                          <strong>{sub.item_limit_snapshot || 0}</strong> items
                        </div>
                      ) : (
                        <div>
                          Validity: <strong>{sub.duration_days_snapshot || 0}</strong> days
                          {sub.end_date && (
                            <span> (Ends: {new Date(sub.end_date).toLocaleDateString()})</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="used-records-stack">
              {data.map((pay) => (
                <div key={pay.id} className="used-record-card">
                  <div className="used-record-card-top">
                    <strong>{pay.subscription?.plan?.name || "Manual Payment"}</strong>
                    <span
                      className={`used-status-pill ${
                        pay.status === "approved"
                          ? "status-published"
                          : pay.status === "pending"
                          ? "status-pending"
                          : "status-expired"
                      }`}
                    >
                      {pay.status}
                    </span>
                  </div>

                  <div className="used-record-details">
                    <div>
                      Amount: <strong>₹{pay.amount}</strong>
                    </div>
                    <div>
                      Transaction UTR: <strong>{pay.transaction_id}</strong>
                    </div>
                    {pay.date_created && (
                      <div>
                        Submitted Date: <strong>{new Date(pay.date_created).toLocaleString("en-IN")}</strong>
                      </div>
                    )}
                    {pay.reviewed_at && (
                      <div>
                        Reviewed Date: <strong>{new Date(pay.reviewed_at).toLocaleString("en-IN")}</strong>
                      </div>
                    )}
                    {pay.rejection_reason && (
                      <div className="used-error-text" style={{ marginTop: 4 }}>
                        Rejection reason: {pay.rejection_reason}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
        <BottomNav />
      </div>
    </AuthGate>
  );
}

/* ── MARKETPLACE PROFILE PAGE — current user's own listings only ── */
function MarketplaceProfile() {
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const itemsQuery = useUsedItemsQuery.myItems({ limit: 50, offset: 0 });
  const subscriptionsQuery = useUsedItemsQuery.subscriptions();

  const myItems = extractList(itemsQuery);
  const allSubs = extractList(subscriptionsQuery);
  const activeSub = allSubs.find((s) => {
    if (String(s.status || "").toLowerCase() !== "active") return false;
    const ptype = String(s.plan_type_snapshot || s.plan?.plan_type || "").toLowerCase();
    if (ptype === "item") return (s.items_used || 0) < (s.item_limit_snapshot || s.plan?.item_limit || 0);
    return !s.end_date || new Date(s.end_date) > new Date();
  });

  // Use mobile from user object or from first listing
  const userMobile = (user?.mobile || myItems[0]?.seller_mobile || "").replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Hi, I found your profile on MasterClick Marketplace.`);

  // Menu row helper
  const MenuRow = ({ icon, label, sublabel, onClick, badge }) => (
    <div className="ump-menu-row" onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
      <div className="ump-menu-icon">{icon}</div>
      <div className="ump-menu-content">
        <span className="ump-menu-label">{label}</span>
        {sublabel && <span className="ump-menu-sublabel">{sublabel}</span>}
      </div>
      {badge && <span className="ump-menu-badge">{badge}</span>}
      {onClick && (
        <svg className="ump-menu-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      )}
    </div>
  );

  return (
    <AuthGate>
      <div className="used-market-page ump-page">
        <Header title="My Account" />
        <main className="used-market-main-content ump-main">

          {/* ── Profile header ── */}
          <div className="ump-header">
            <div className="ump-avatar">
              <PersonIcon style={{ fontSize: 38, color: "var(--used-primary)" }} />
            </div>
            <div className="ump-header-info">
              <h2 className="ump-name">{user?.name || user?.username || "Marketplace User"}</h2>
              {user?.mobile && (
                <div className="ump-mobile">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" style={{ marginRight: 4 }}>
                    <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12" y2="18" strokeWidth="2.5" />
                  </svg>
                  {user.mobile}
                </div>
              )}
              {activeSub && (
                <div className="ump-plan-badge">
                  ✓ {activeSub.plan?.name || "Active Plan"}
                  {activeSub.plan_type_snapshot === "item" && (
                    <span> · {Math.max(0, (activeSub.item_limit_snapshot || 0) - (activeSub.items_used || 0))} left</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Account menu rows ── */}
          <div className="ump-section">
            <MenuRow
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/></svg>}
              label="My Listings"
              sublabel={`${myItems.length} item${myItems.length !== 1 ? "s" : ""}`}
              badge={myItems.filter(i => i.status === "published").length || undefined}
              onClick={() => history.push("/used-items/my-items")}
            />
            <MenuRow
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
              label="Sell Item"
              sublabel="Create a new listing"
              onClick={() => history.push("/used-items/sell")}
            />
            <MenuRow
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M20 12V22H4V12"/><path d="M22 7H2v5h20V7z"/><path d="M12 22V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>}
              label="Subscriptions"
              sublabel={activeSub ? `Active: ${activeSub.plan?.name || "Plan"}` : "No active plan"}
              onClick={() => history.push("/used-items/my-subscriptions")}
            />
            <MenuRow
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><rect x="2" y="5" width="20" height="14" rx="2.5"/><line x1="2" y1="10" x2="22" y2="10"/><rect x="6" y="14" width="4" height="2" rx="0.5" fill="currentColor" stroke="none"/></svg>}
              label="Payment History"
              sublabel="View past payments"
              onClick={() => history.push("/used-items/my-payments")}
            />
            <MenuRow
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>}
              label="Favorites"
              sublabel="Saved items"
              onClick={() => history.push("/used-items/visited")}
            />
            <MenuRow
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><circle cx="12" cy="12" r="3"/><path d="M20.188 10.934c.326.179.326.625 0 .804L12.94 15.8c-.351.192-.79-.048-.79-.454V8.654c0-.406.439-.646.79-.454z" fill="currentColor" stroke="none"/><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>}
              label="Recently Visited"
              sublabel="Browse history"
              onClick={() => history.push("/used-items/visited")}
            />
          </div>

          {/* ── LOGOUT option directly above Account section ── */}
          <div className="ump-section" style={{ marginBottom: "8px" }}>
            <MenuRow
              icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>}
              label="LOGOUT"
              sublabel="Sign out of your account"
              onClick={() => {
                // Clear authentication data
                localStorage.removeItem("accessToken");
                localStorage.removeItem("user");
                localStorage.removeItem("refreshToken");
                // Redirect to home page
                history.push("/");
              }}
            />
          </div>

          {/* ── Account details section ── */}
          <div className="ump-section-label">Account</div>
          <div className="ump-section">
            {user?.name && (
              <MenuRow
                icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
                label="Name"
                sublabel={user.name}
              />
            )}
            {user?.mobile && (
              <MenuRow
                icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18" strokeWidth="2.5"/></svg>}
                label="Mobile Number"
                sublabel={user.mobile}
              />
            )}
            {user?.email && (
              <MenuRow
                icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>}
                label="Email"
                sublabel={user.email}
              />
            )}
            {user?.username && user.username !== user.name && (
              <MenuRow
                icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>}
                label="Username"
                sublabel={user.username}
              />
            )}
          </div>

          {/* ── Contact buttons: Chat → Call → WhatsApp — above My Listings ── */}
          {userMobile && (
            <div className="ump-contact-row">
              <button className="ump-contact-btn ump-chat" onClick={() => alert("Chat feature coming soon!")}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Chat
              </button>
              <a className="ump-contact-btn ump-call" href={`tel:${userMobile}`}>
                <PhoneIcon style={{ fontSize: 18 }} />
                Call
              </a>
              <a className="ump-contact-btn ump-whatsapp" href={`https://wa.me/${userMobile}?text=${waMsg}`} target="_blank" rel="noreferrer">
                <WhatsAppIcon style={{ fontSize: 18 }} />
                WhatsApp
              </a>
            </div>
          )}

          {/* ── Recent listings preview ── */}
          {myItems.length > 0 && (
            <>
              <div className="ump-section-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>My Listings</span>
                <button className="used-text-btn" style={{ fontSize: 12 }} onClick={() => history.push("/used-items/my-items")}>View all</button>
              </div>
              <div className="used-my-items-list" style={{ marginBottom: 24 }}>
                {myItems.slice(0, 3).map((item) => {
                  const statusPill = item.status === "published" ? "status-published" : item.status === "pending" ? "status-pending" : "status-expired";
                  return (
                    <div key={item.id} className="used-my-item-row" style={{ cursor: "pointer" }} onClick={() => history.push(`/used-items/items/${item.id}`)}>
                      <img src={item.images?.[0]?.image || "/thumbnail.png"} alt="" className="used-my-item-img" onError={(e) => { e.target.src = "/thumbnail.png"; }} />
                      <div className="used-my-item-info">
                        <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "var(--used-primary)" }}>{item.title}</h4>
                        <div className="used-my-item-meta">
                          <span className={`used-status-pill ${statusPill}`}>{item.status}</span>
                          <strong style={{ color: "var(--used-primary)" }}>₹{Number(item.price).toLocaleString("en-IN")}</strong>
                        </div>
                        {item.location && <div style={{ fontSize: 11, color: "var(--used-muted)", marginTop: 2 }}>{item.location}</div>}
                      </div>
                      <div className="used-my-item-actions">
                        <button className="used-icon-action-btn edit" onClick={(e) => { e.stopPropagation(); history.push(`/used-items/items/my/${item.id}`); }} title="Edit">
                          <EditIcon style={{ fontSize: 18 }} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {myItems.length === 0 && !itemsQuery.isLoading && (
            <div style={{ textAlign: "center", padding: "32px 16px 24px" }}>
              <div style={{ fontSize: 44, marginBottom: 10 }}>🛍️</div>
              <p style={{ color: "var(--used-muted)", fontSize: 13, margin: "0 0 16px" }}>No listings yet.</p>
              <button className="used-primary-btn" onClick={() => history.push("/used-items/sell")}>+ Create Your First Listing</button>
            </div>
          )}

        </main>
        <BottomNav />
      </div>
    </AuthGate>
  );
}

/* ── VISITED / HISTORY PAGE ── */
function Visited() {
  const history = useHistory();
  const [visits, setVisits] = React.useState([]);
  const { favorites, isFav, toggle } = useFavorites();

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(VISITED_KEY);
      setVisits(raw ? JSON.parse(raw) : []);
    } catch (_) {
      setVisits([]);
    }
  }, []);

  const clearAllVisits = () => {
    localStorage.removeItem(VISITED_KEY);
    setVisits([]);
  };

  return (
    <div className="used-market-page">
      <Header title="Favorites & Visited" />
      <main className="used-market-main-content">

        {/* ── FAVORITES SECTION ── */}
        <div className="used-section-header" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            ♥ Favorites
          </h3>
          <span className="used-count-badge">{favorites.length} saved</span>
        </div>

        {favorites.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px 0 24px", color: "var(--used-muted)", fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 6 }}>♡</div>
            No Favorites Yet — tap ♡ on any listing to save it here.
          </div>
        ) : (
          <div className="used-my-items-list" style={{ marginBottom: 24 }}>
            {favorites.map((f) => {
              const faved = isFav(f.id);
              return (
                <div
                  key={f.id}
                  className="used-my-item-row"
                  style={{ cursor: "pointer" }}
                  onClick={() => history.push(f.path)}
                >
                  <img
                    src={f.image || "/thumbnail.png"}
                    alt={f.title}
                    className="used-my-item-img"
                    onError={(e) => { e.target.src = "/thumbnail.png"; }}
                  />
                  <div className="used-my-item-info">
                    <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "var(--used-primary)" }}>{f.title}</h4>
                    <div className="used-my-item-meta">
                      {f.category && <span className="used-detail-cat-badge" style={{ marginRight: 6 }}>{f.category}</span>}
                      <strong style={{ color: "var(--used-primary)" }}>₹{Number(f.price).toLocaleString("en-IN")}</strong>
                    </div>
                    {f.location && <div style={{ fontSize: 11, color: "var(--used-muted)", marginTop: 2 }}>{f.location}</div>}
                  </div>
                  {/* Remove from favorites */}
                  <button
                    className="used-fav-btn faved"
                    style={{ position: "static", marginLeft: 8, flexShrink: 0 }}
                    aria-label="Remove from favorites"
                    onClick={(e) => { e.stopPropagation(); toggle(f); }}
                  >
                    ♥
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* ── DIVIDER ── */}
        <div style={{ borderTop: "1px solid var(--used-border)", margin: "4px 0 18px" }} />

        {/* ── VISITED SECTION ── */}
        <div className="used-section-header" style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Recently Visited</h3>
          {visits.length > 0 && (
            <button className="used-text-btn" onClick={clearAllVisits}>Clear All</button>
          )}
        </div>

        {visits.length === 0 ? (
          <Message>No visited items yet. Browse and open an item to see it here.</Message>
        ) : (
          <div className="used-my-items-list">
            {visits.map((v) => (
              <div
                key={`${v.id}-${v.visitedAt}`}
                className="used-my-item-row"
                style={{ cursor: "pointer" }}
                onClick={() => history.push(v.path)}
              >
                <img
                  src={v.image || "/thumbnail.png"}
                  alt={v.title}
                  className="used-my-item-img"
                  onError={(e) => { e.target.src = "/thumbnail.png"; }}
                />
                <div className="used-my-item-info">
                  <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700 }}>{v.title}</h4>
                  <div className="used-my-item-meta">
                    {v.category && (
                      <span className="used-detail-cat-badge" style={{ marginRight: 6 }}>{v.category}</span>
                    )}
                    <strong style={{ color: "var(--used-primary)" }}>
                      ₹{Number(v.price).toLocaleString("en-IN")}
                    </strong>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--used-muted)", marginTop: 3 }}>
                    Visited {new Date(v.visitedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}

/* ── MAIN COMPONENT ROUTER DISPATCH ── */
export default function UsedItems() {
  const path = useLocation().pathname;

  if (/\/items\/\d+$/.test(path)) return <Detail />;
  if (path.endsWith("/sell/item")) return <ItemForm />;
  if (/\/items\/my\/\d+$/.test(path)) return <ItemForm edit />;
  if (path.endsWith("/my-items")) return <MyList type="items" />;
  if (path.endsWith("/my-subscriptions")) return <MyList type="subscriptions" />;
  if (path.endsWith("/my-payments")) return <MyList type="payments" />;
  if (path.endsWith("/visited")) return <Visited />;
  if (path.endsWith("/my-profile")) return <MarketplaceProfile />;
  if (path.startsWith("/used-items/sell")) return <ItemForm />;

  return <Browse />;
}
